/**
 * DashboardManager
 *
 * Manages the dashboard display, including navigation cards and recent activity.
 * Handles user interactions and coordinates with the chat interface.
 * Uses BadgeService for nav card status (discovery badge).
 */

import page from '../vendor/page.js';
import { API_URL, API, fetchWithTimeout } from './api.js';
import { DiscoveryAPI } from './discoveryApi.js';
import { escapeHtml, eventBus, isGuestName } from './utils.js';
import { formatTimeAgo } from '../utils/timeFormat.js';
import {
  ORIENTATION_PROGRESS_STEPS,
  ORIENTATION_TOTAL_STEPS,
  ORIENTATION_PHASES
} from '../orchestration/orientationContent.js';
import { renderRowKebab, RowMenuController, positionMenuFixedBelow } from '../components/optionsMenu.js';
import { modalService } from '../services/ModalService.js';
import {
  emitContentArchived,
  CONTENT_STARRED,
  CONTENT_ARCHIVED,
  CONTENT_DELETED,
  CONTENT_VIEWED,
  CONTENT_FLAGGED,
  reduceNormalizedItems
} from './contentSync.js';
import { notifyArchived, notifyArchiveFailed } from '../utils/archiveFeedback.js';
import { searchSources, stampContinuationTitles, PRACTICE_SOURCE, isRecentsEligible, TYPE_ICON, guideCoverHtml, renderGuideCover } from '../components/searchSources.js';
import { openContentItem } from '../components/contentNavigation.js';
import {
  STAR_ICON,
  buildContentRowMenuItems,
  confirmDeleteContentItem,
  discussContentItem,
  runStar,
  runArchive,
  runMarkViewed,
  runToggleFlag,
  runDelete
} from '../components/contentRowActions.js';

/**
 * localStorage key for a guest's orientation-row dismissal. Guests never
 * reach the backend fetch (loadRecentActivity skips it entirely), so there is
 * no server-side DashboardDismissal row for them — persistence is local
 * instead. Guests are single-device by nature, so no cross-device concern.
 * Same 24h semantics as the server-side Tier-2 quick-popback window.
 */
const GUEST_ORIENTATION_DISMISS_KEY = 'dashboard.orientationDismissedUntil';

/**
 * Action label shown as the first row of each recommended-activity item — the
 * verb describing what tapping the item does. Orientation is intentionally
 * absent: it has no action-label row (its name leads instead) when unfeatured
 * — but orientation is ALWAYS the featured winner when present (top of
 * FEATURED_RULES), so in practice it always gets a row-1 label, from
 * FEATURED_LABELS below rather than from here.
 */
const ACTION_LABELS = {
  path: 'Work on discovery path',
  guide: 'View guide',
  practice: 'Report back on experiment',
  active_advice_session: 'Resume session',
  guest_conversion: 'Keep your profile'
  // partner_invite and sharing are deliberately absent: their titles are already
  // the verb ("Invite your partner", "Review what {partner} sees"), so an action
  // label above them would just repeat it. Orientation omits one for the same
  // reason. intake_issue is absent because its "Discuss" badge already says
  // what tapping does.
  //
  // partner_notes is absent on different grounds: its title IS a noun ("Partner
  // Notes (new feature)"), but the row is an introduction rather than a task —
  // an imperative above it would overstate what tapping it does (it opens an
  // explanation, not a composer). Its subtitle carries the invitation instead.
};

/**
 * Row-1 text override for whichever row wins the featured slot, keyed by
 * type — replaces ACTION_LABELS (or fills the gap where ACTION_LABELS has no
 * entry, e.g. orientation) whenever a row is featured. Always rendered in the
 * same orange emphasis regardless of type — see the featured-label lookup in
 * renderRecentActivity(). Types absent here (orientation, guest_conversion,
 * path, sharing) default to "High Priority" below.
 */
const FEATURED_LABELS = {
  intake_issue: 'Start Here',
  partner_invite: 'Recommended',
  partner_notes: 'New Feature'
};

/**
 * Recommended-activity rows eligible for the featured treatment (white card with
 * a border), in priority order — the earliest match wins. At most ONE row per
 * render carries it, so the section has a single visual focal point rather than
 * a wall of competing cards.
 *
 * Selection happens here rather than in the backend service because two of the
 * eligible types (`orientation`, `guest_conversion`) are synthesized client-side
 * in `loadRecentActivity` and never reach `recentActivityService` — this is the
 * only place that sees the merged list.
 *
 * Most of these can't actually co-occur, so the ranking settles fewer contests
 * than it looks: `guest_conversion` implies a guest, and the guest branch of
 * `loadRecentActivity` skips the backend fetch entirely, so it is always alone
 * (`orientation` is excluded from that list too — the two require opposite
 * `onboardingStep` values, and Foundation is filtered out entirely until
 * onboarding completes — see the suppression in `loadRecentActivity`). So
 * Foundation and orientation can never contend, and Foundation completes (and
 * stops matching) before a user reaches `foundation-2`, which is not itself
 * featured-eligible. `orientation` likewise can't meet `partner_invite`,
 * `sharing`, or `partner_notes`, all three of which require a complete
 * onboarding. And `partner_invite` requires the ABSENCE of a coupleId, while
 * `sharing`, partner-waiting paths, and `partner_notes` all require one — so
 * `partner_invite` can't contend with any of those three either. What remains
 * in practice is `intake_issue` against whatever else is present, and
 * `sharing` / `partner_notes` against a partner-waiting path, a
 * still-incomplete Foundation, or each other.
 *
 * A `match` predicate narrows a type when only some of its rows qualify.
 */
const FEATURED_RULES = [
  // Blocks everything else — the user hasn't finished onboarding.
  { type: 'orientation' },
  // The profile exists but nothing is keeping it.
  { type: 'guest_conversion' },
  // The pinned first path — gates the whole couples product (see
  // PINNED_PATH_IDS in recentActivityService), so it outranks the
  // partner-waiting case below even though both are path rows. Scoped to
  // `foundation` specifically, not `foundation-2` (never featured-eligible —
  // it's the deliberately lower-key "continuing" step, not the onboarding gate).
  { type: 'path', match: item => item.metadata?.pathId === 'foundation' },
  // The only row with an external clock: another person is already finished and
  // blocked waiting. In-progress paths are NOT eligible — only partner-waiting.
  { type: 'path', match: item => item.metadata?.isPartnerWaiting === true },
  // Pending consent, gating shared understanding.
  { type: 'sharing' },
  // A concern the user raised themselves; decays by age.
  { type: 'intake_issue' },
  // A standing invitation with no clock on it.
  { type: 'partner_invite' },
  // Lowest priority: a standing, low-urgency feature introduction, not a task
  // or anything blocking another person. Scoped to the intro row specifically
  // (`getPartnerNotesActivityItem`'s `partner:notes-intro` id) — the separate
  // "you have an unread note" aggregate row (`partner:notes-unread`) is a
  // stronger signal and deliberately NOT included here yet; that's a distinct
  // decision to make later, not bundled into this one.
  { type: 'partner_notes', match: item => item.id === 'partner:notes-intro' }
];

/**
 * Kebab-menu items per recommended-activity type. Keyed by `type`, in render
 * order — see the dashboard-kebab-dismissals plan §2. A type with no entry
 * here (`guest_conversion`) renders no
 * kebab at all. `partner_notes` is special-cased in {@link menuConfigFor}
 * since its two ids (`partner:notes-intro`, `partner:notes-unread`) need
 * different "open" labels.
 *
 * Every item carries an explicit `icon` override except `row-dismiss`, whose
 * bare verb IS mapped in `optionsMenu.js`'s `MENU_ICON` (`eye-off`, added
 * alongside this feature) — the map is keyed by the bare action verb after
 * stripping the `row-`/`select-` prefix, and no other verb here is mapped.
 * The `row-open` icon rule: `chat-bubble` for every open that loads into the
 * chat window (path, practice check-in, resume, intake issue, orientation);
 * opens that land on a page instead carry that destination's own glyph.
 */
const MENU_CONFIG = {
  path: [
    { action: 'row-open', label: 'Work on this', icon: 'chat-bubble' },
    { action: 'row-dismiss', label: 'Dismiss' }
  ],
  guide: [
    { action: 'row-open', label: 'View guide', icon: 'guide' },
    { action: 'row-dismiss', label: 'Dismiss' }
  ],
  practice: [
    { action: 'row-open', label: 'Check in', icon: 'chat-bubble' },
    { action: 'row-view-practice', label: 'View experiment', icon: 'eye' },
    { action: 'row-archive-practice', label: 'Archive', icon: 'archive' },
    { action: 'row-dismiss', label: 'Dismiss' }
  ],
  active_advice_session: [
    { action: 'row-open', label: 'Continue chat', icon: 'chat-bubble' },
    { action: 'row-end_session', label: 'End session', icon: 'signout' }
  ],
  sharing: [
    { action: 'row-open', label: 'Get started', icon: 'partner' },
    { action: 'row-dismiss', label: 'Dismiss' }
  ],
  partner_invite: [
    { action: 'row-open', label: 'Get started', icon: 'partner' },
    { action: 'row-dismiss', label: 'Dismiss' }
  ],
  intake_issue: [
    { action: 'row-open', label: 'Discuss now', icon: 'chat-bubble' },
    { action: 'row-dismiss', label: 'Dismiss' }
  ],
  orientation: [
    { action: 'row-open', label: 'Finish orientation', icon: 'chat-bubble' },
    { action: 'row-dismiss', label: 'Dismiss' }
  ]
};

/**
 * Resolve an activity item's kebab-menu items, or `null` for a type with no
 * kebab. `partner_notes` distinguishes its two ids by "open" label (Learn
 * more vs. View note); every other type reads straight off {@link MENU_CONFIG}.
 * @param {Object} item - Activity item
 * @returns {Array<Object>|null}
 */
function menuConfigFor(item) {
  if (item.type === 'partner_notes') {
    // startsWith, not ===: the production id is exactly 'partner:notes-unread',
    // but the devtools demo fixtures suffix it ('-1', '-n') to show both title
    // variants at once — an exact match would silently hand those rows the
    // intro menu, so the demo would never preview the real unread kebab.
    const isUnread = item.id.startsWith('partner:notes-unread');
    return [
      { action: 'row-open', label: isUnread ? 'View note' : 'Learn more', icon: isUnread ? 'mail' : 'info' },
      { action: 'row-dismiss', label: 'Dismiss' }
    ];
  }
  return MENU_CONFIG[item.type] || null;
}

/**
 * Display name per Recommended/Recent/Starred view — the active view's label
 * doubles as the switcher button's own text (see `_renderActivityViewSwitcherHtml`).
 */
const ACTIVITY_VIEWS = ['recommended', 'recent', 'starred'];
const ACTIVITY_VIEW_LABELS = {
  recommended: 'Recommended',
  recent: 'Recent',
  starred: 'Starred'
};

export class DashboardManager {
  /**
   * @param {Object} eventBus - Global event bus for component communication
   * @param {Object} apiClient - API client for backend requests
   * @param {Object} currentUser - Current authenticated user object
   */
  constructor(eventBus, apiClient, _currentUser = null) {
    this.eventBus = eventBus;
    this.apiClient = apiClient;
    this.container = null;
    this.userName = null;
    this.partnerName = null;
    this.suggestedPrompts = [];
    this.discoveryAPI = new DiscoveryAPI();

    // Nav card status data - populated from BadgeService
    this.navCardStatus = {
      discovery: { tag: null, statusText: null },
      advice: { tag: null, statusText: null }
    };

    // Recent Activity data
    this.recentActivityItems = [];
    this.recentActivityContainer = null;
    // Active server-stored dismissals for client-synthesized rows (today just
    // `orientation`), from the last GET's `dismissals` field — see loadRecentActivity.
    this._orientationDismissal = null;
    // True after dev.dashboard.demo()/feature()/featuredGallery() load mock
    // rows; cleared on the next real loadRecentActivity(). Gates the kebab
    // action handlers (dismiss/archive/end-session) so they short-circuit to
    // a local-only change instead of POSTing a mock id the backend would 400.
    this._demoMode = false;
    /**
     * Row options menu open/close controller (menu is a child of its
     * `.dashboard-recent-activity__item`). `clipContainer` opts into the
     * vertical flip-up guard: `#dashboard-recent-activity` is the list's own
     * `overflow: auto` scrollport (dashboard.css), which clips a downward-
     * opening menu near the bottom of the list — z-index cannot beat that,
     * only repositioning the menu can.
     */
    this._rowMenu = new RowMenuController({
      container: () => this.recentActivityContainer,
      keyAttr: 'data-item-id',
      rowSelector: '.dashboard-recent-activity__item',
      hoverClose: true,
      clipContainer: () => this.recentActivityContainer
    });

    // Recommended/Recent/Starred view switcher (see §3 of the
    // dashboard-recommended-dropdown plan). All five reset at the top of
    // returnToDashboard(), before its first await — see that method.
    this.activityView = 'recommended';
    // SearchItems for the Recent/Starred views — session/note/guide/practice
    // via searchSources + PRACTICE_SOURCE. null = not yet loaded this visit
    // (or a total load failure — see _loadLibraryItems); [] = loaded and
    // genuinely empty.
    this.libraryItems = null;
    // In-flight-load guard + promise-identity commit gate (see _loadLibraryItems).
    this._libraryLoadPromise = null;
    // Set when every attempted library source failed (see _fetchLibraryItems).
    this.libraryLoadFailed = false;
    // Rows revealed in the Recent/Starred views; +10 per "Show more" click.
    this.activityVisibleCount = 10;
    // Guards a library-row kebab action in flight (star/archive/delete/…) —
    // mirrors SidebarRecentsController._actionInFlight so a rapid second
    // click before the first call resolves can't double-fire. Not reset in
    // returnToDashboard(): a library row action can't outlive the visit that
    // started it (the view resets to 'recommended', and library rows are the
    // only surface that sets this flag).
    this._libraryActionInFlight = false;
    // Whether the view-switcher's dropdown menu is currently open — drives
    // the document-level outside-click listener's lifecycle (see
    // _openActivityViewMenu/_closeActivityViewMenu).
    this._activityViewMenuOpen = false;
    this._boundCloseActivityViewMenu = () => this._closeActivityViewMenu();
    this._boundActivityViewKeydown = (e) => this._handleActivityViewKeydown(e);

    this.promptOffsetObserver = null;
    this._updatePromptOffset = null;
    this.suggestedPromptsObserver = null;
    this._updateSuggestedPromptsHeight = null;
    this.dashboardPickerItems = [];
    this.discoveryPromptItems = null;
    this.practicePromptItems = null;
    this.dashboardPickerCounts = {};
    this.activeDashboardPromptId = null;
    this.activeShortcutPromptText = null;
  }

  /**
   * Current user, read live from the app rather than captured at construction.
   *
   * Guest→account conversion happens in place, with no page reload and no
   * re-construction of this manager, so a boot-time snapshot would keep naming
   * the user "guest" and keep `coupleId`/`userId` at their pre-conversion
   * values for the rest of the visit. Same reasoning as
   * AccountSettingsSection's `user` getter.
   * @returns {Object|null}
   */
  get currentUser() {
    return window.app?.currentUser || null;
  }

  /** @returns {string|null} Live couple id. */
  get coupleId() {
    return this.currentUser?.coupleId || null;
  }

  /** @returns {string|null} Live user id. */
  get userId() {
    return this.currentUser?.id || null;
  }

  /**
   * Initialize the dashboard
   * Fetches user data and suggested prompts, then renders
   */
  async initialize() {
    console.log('🎯 DashboardManager: Initializing...');
    this.container = document.getElementById('dashboard');
    this.recentActivityContainer = document.getElementById('dashboard-recent-activity');

    if (!this.container) {
      console.error('Dashboard container not found');
      return;
    }

    // Get user name and partner name from session storage or user profile
    await this.loadUserName();
    console.log('🎯 DashboardManager: User name loaded:', this.userName);

    // Load partner name for prompt interpolation
    await this.loadPartnerName();
    console.log('🎯 DashboardManager: Partner name loaded:', this.partnerName);

    // Fetch suggested prompts for dashboard display
    await this.loadSuggestedPrompts();
    console.log('🎯 DashboardManager: Suggested prompts loaded:', this.suggestedPrompts.length, 'prompts');

    // Load nav card status from BadgeService (sync - BadgeService already loaded)
    this.loadNavCardStatus();
    console.log('🎯 DashboardManager: Nav card status:', this.navCardStatus);

    // Load recent activity items
    await this.loadRecentActivity();
    console.log('🎯 DashboardManager: Recent activity loaded:', this.recentActivityItems.length, 'items');

    // Render dashboard
    this.render();
    console.log('🎯 DashboardManager: Dashboard rendered');

    // Render recent activity (separate container)
    this.renderRecentActivity();

    // Setup event listeners
    this.setupEventListeners();
    this.setupPromptOffsetTracking();

    // Pre-fetch picker datasets so the suggested-prompt buttons can show
    // item counts + badges, and the dropdown opens instantly on first click.
    this.loadDashboardPickerCounts();
  }

  /**
   * Load user name from user data
   */
  async loadUserName() {
    // Greeting uses firstName only. Username is an auto-generated login
    // identifier (email-prefix slug), never appropriate as a salutation.
    // When firstName is missing, leave userName empty so the render branch
    // drops the salutation entirely — matches orientationContent.js convention
    // (nameless variants omit "Hi {name}" rather than fall back to "Hi there").
    if (this.currentUser) {
      this.userName = (this.currentUser.firstName && !isGuestName(this.currentUser.firstName))
        ? this.currentUser.firstName
        : '';
      return;
    }

    try {
      const result = await API.checkAuth();
      const firstName = result?.user?.firstName;
      this.userName = (firstName && !isGuestName(firstName)) ? firstName : '';
    } catch (error) {
      console.error('Error loading user name:', error);
      this.userName = '';
    }
  }

  /**
   * Load partner name from couple data
   */
  async loadPartnerName() {
    // Try to get partner name from couple data if available
    if (this.currentUser?.partner?.firstName) {
      this.partnerName = this.currentUser.partner.firstName;
      return;
    }

    // Try to get from BadgeService if available (it has discovery status with partner name)
    if (window.badgeService) {
      const discoveryStatus = window.badgeService.getDiscoveryStatus();
      if (discoveryStatus?.partnerWaitingName) {
        this.partnerName = discoveryStatus.partnerWaitingName;
        return;
      }
    }

    // Default fallback
    this.partnerName = null;
  }

  /**
   * Load suggested prompts (static conversation starters).
   * Items with promptText trigger curated autocomplete options; items without it
   * are visual placeholders until their destination behavior is added.
   */
  async loadSuggestedPrompts() {
    // Static shortcut buttons - promptText must match SHORTCUT_TRIGGERS in AutocompleteService.
    // `section` maps the button to its BadgeService section for corner-dot lookups.
    this.suggestedPrompts = [
      { id: 'discovery', displayText: 'Discovery', pickerType: 'discovery', section: 'discovery' },
      { id: 'practices', displayText: 'Experiments', pickerType: 'practices', section: 'practices' },
      { id: 'feeling', displayText: "I'm feeling...", promptText: "I'm feeling...", mode: 'ADVICE' }
      // Hidden — kept for easy reinstatement:
      // { id: 'situation', displayText: 'Discuss...', promptText: 'Discuss a situation...', mode: 'ADVICE' }
    ];
  }

  /**
   * Load recent activity items from API
   * Fetches recommended activity items with progress/status
   */
  async loadRecentActivity() {
    // A real fetch always supersedes any devtools preview state.
    this._demoMode = false;

    try {
      let items = [];

      // Guests have no account-scoped activity and the route is auth-only, so
      // fetching would only produce a 401 in the console. Their synthetic items
      // below are the whole list.
      if (window.app?.isGuest !== true) {
        const response = await fetchWithTimeout(`${API_URL}/dashboard/recent-activity`, {
          credentials: 'include'
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data?.items) {
            items = result.data.items;
          }
          this._orientationDismissal = result.data?.dismissals?.orientation || null;
        } else {
          console.warn('🎯 DashboardManager: Failed to load recent activity:', response.status);
        }
      }

      // Suppress the Foundation path recommendation until orientation completes
      // so a brand-new user isn't pulled toward Foundation before finishing the
      // intro flow. Mirrors the BadgeService onboardingStep gate.
      if (window.app?.currentUser?.onboardingStep !== 'complete') {
        items = items.filter(item => !(item.type === 'path' && item.metadata?.pathId === 'foundation'));
      }

      // Prepend client-derived items. Backend recent-activity stays unaware —
      // both signals are purely a function of `currentUser`. The two are
      // mutually exclusive by construction (one requires orientation
      // incomplete, the other requires it complete).
      const synthetic = [
        this._buildGuestConversionActivityItem(),
        this._buildOrientationActivityItem()
      ].filter(Boolean);
      this.recentActivityItems = [...synthetic, ...items];
    } catch (error) {
      console.error('🎯 DashboardManager: Error loading recent activity:', error);
      this.recentActivityItems = [];
    }
  }

  /**
   * Rebuild the recent-activity list from live user state and re-render it.
   *
   * The synthetic rows (guest conversion, resume orientation) are pure
   * functions of `window.app.currentUser` / `isGuest`, so an auth-state change
   * that happens in place — guest conversion, orientation completion — has to
   * come through here to be reflected; nothing re-derives them on its own.
   *
   * @returns {Promise<void>}
   */
  async refreshRecentActivity() {
    await this.loadRecentActivity();
    this.renderRecentActivity();
  }

  /**
   * Build the guest conversion card: the standing offer for someone who ran the
   * orientation demo and declined the account at its close.
   *
   * They landed here with a real profile and no way back into conversion except
   * by walking into a locked door, so the dashboard has to carry the invitation.
   * Returns null for anyone who is not a completed-orientation guest.
   *
   * @returns {Object|null}
   * @private
   */
  _buildGuestConversionActivityItem() {
    const user = this.currentUser;
    if (window.app?.isGuest !== true) return null;
    if (user?.onboardingStep !== 'complete') return null;

    return {
      id: 'guest_conversion',
      type: 'guest_conversion',
      title: 'Create your free account',
      subtitle: 'Your profile is started — an account keeps it.',
      progress: null,
      // Same low-prominence blue dot + label the partner-invite nudge uses:
      // this is a short thing worth doing, not an alarm.
      badges: [{ text: '2 MIN', color: 'blue' }],
      metadata: {}
    };
  }

  /**
   * Build a synthetic "Resume orientation" activity item from the current user
   * state. Returns null unless the user is dismissed-but-incomplete.
   * @returns {Object|null}
   * @private
   */
  _buildOrientationActivityItem() {
    const user = window.app?.currentUser;
    if (!user) return null;
    if (user.onboardingStep === 'complete') return null;
    if (!user.orientationDismissedAt) return null;
    if (this._isOrientationSnoozed()) return null;

    const phase = user.onboardingStep || ORIENTATION_PHASES.INTRODUCTION;
    const currentStep = ORIENTATION_PROGRESS_STEPS[phase] || 1;
    const totalSteps = ORIENTATION_TOTAL_STEPS;
    const progressPct = Math.round((currentStep / totalSteps) * 100);

    return {
      id: 'orientation',
      type: 'orientation',
      title: 'Finish Orientation',
      subtitle: `Step ${currentStep} of ${totalSteps}`,
      progress: progressPct,
      metadata: { phase }
    };
  }

  /**
   * Whether the client-synthesized orientation row is currently within an
   * active dismissal window (kebab "Dismiss", Tier 2: 24h quick pop-back).
   * Registered users' window comes from the last GET's `dismissals.orientation`
   * (server-stored); guests — who never reach that fetch — read their own
   * localStorage window instead (see {@link GUEST_ORIENTATION_DISMISS_KEY}).
   * @returns {boolean}
   * @private
   */
  _isOrientationSnoozed() {
    let snoozeUntil = null;
    if (window.app?.isGuest === true) {
      try {
        snoozeUntil = localStorage.getItem(GUEST_ORIENTATION_DISMISS_KEY);
      } catch {
        snoozeUntil = null;
      }
    } else {
      snoozeUntil = this._orientationDismissal?.snoozeUntil || null;
    }
    return Boolean(snoozeUntil) && Date.now() < new Date(snoozeUntil).getTime();
  }

  /**
   * Pick the single row that gets the featured treatment, by walking
   * {@link FEATURED_RULES} in priority order and taking the first match.
   *
   * Returns an id rather than an index so the caller stays correct if the list
   * is ever reordered between selection and render. When a type matches more
   * than one row (two partner-waiting paths), only the first is featured.
   *
   * @param {Array<Object>} items - The merged activity list
   * @returns {string|null} The featured item's id, or null if none qualify
   * @private
   */
  _selectFeaturedItemId(items) {
    for (const rule of FEATURED_RULES) {
      const match = items.find(item =>
        item.type === rule.type && (!rule.match || rule.match(item))
      );
      if (match) return match.id;
    }
    return null;
  }

  /**
   * Move the featured row to the front of the render order, so the one card
   * carrying visual prominence is not stranded mid-list. `sharing` sits below
   * unlimited unviewed guides and `partner_invite` below those AND partner-
   * waiting paths, so without this a bordered card can land at position 6+.
   *
   * It stops short of an open ADVICE session, which keeps rank 1. That row
   * exists so a returning user re-enters the live conversation instead of
   * starting a new one and silently closing it; a standing nudge must not
   * displace the single item whose loss costs the user work.
   *
   * Returns a new array — `this.recentActivityItems` keeps its priority order,
   * which is what the click handler searches by id.
   *
   * @param {Array<Object>} items - The merged activity list, in priority order
   * @param {string|null} featuredItemId - Result of {@link _selectFeaturedItemId}
   * @returns {Array<Object>} Render order, featured row hoisted
   * @private
   */
  _hoistFeaturedItem(items, featuredItemId) {
    if (!featuredItemId) return items;

    const featured = items.find(item => item.id === featuredItemId);
    if (!featured) return items;

    const rest = items.filter(item => item.id !== featuredItemId);
    // Insert after a leading resume card if one is present, else at the front.
    const insertAt = rest[0]?.type === 'active_advice_session' ? 1 : 0;
    return [...rest.slice(0, insertAt), featured, ...rest.slice(insertAt)];
  }

  /**
   * Render recent activity section
   * Renders into the separate #dashboard-recent-activity container
   */
  renderRecentActivity() {
    if (!this.recentActivityContainer) return;

    // This rebuilds the container's innerHTML wholesale — closing first keeps
    // an open menu's controller state from stranding on the detached nodes
    // that innerHTML is about to replace. Same reasoning for the view-switcher
    // menu: a re-render can never strand its document-level outside-click
    // listener on nodes about to be replaced.
    this._rowMenu?.close();
    this._closeActivityViewMenu();

    // Hide the whole section only for a guest with an empty (synthetic-only)
    // feed (CSS :empty also handles this) — guests never get the view
    // switcher (see _renderActivityLabelHtml, which renders a static label
    // for them), so there is nothing worth keeping alive. An authenticated
    // user with an empty Recommended feed still renders the switcher, below
    // — see _renderRecommendedActivityHtml's own empty-row fallback. Recent/
    // Starred never reach this guard: `recentActivityItems` is the
    // Recommended feed's own array, unrelated to the library content those
    // views render. This also covers the `orientation:completed` re-render
    // path, which does not reset `activityView`.
    if (this.recentActivityItems.length === 0 && window.app?.isGuest === true) {
      this.recentActivityContainer.innerHTML = '';
      return;
    }

    const bodyHtml = this.activityView === 'recommended'
      ? this._renderRecommendedActivityHtml()
      : this._renderLibraryRowsHtml();

    this.recentActivityContainer.innerHTML = `${this._renderActivityLabelHtml()}${bodyHtml}`;
  }

  /**
   * Build the Recommended view's body: the synthetic + backend activity rows,
   * with featured-row hoisting/labeling and per-type badges/subtitles — the
   * pre-existing recommended-activity render path (unchanged), plus a muted
   * fallback row for the empty case. That case is reachable in ordinary use
   * now that kebab dismissals landed (an authenticated user can dismiss every
   * row); the empty-guard in `renderRecentActivity()` only blanks the section
   * for guests, so the view switcher must keep rendering here.
   * @returns {string} HTML
   * @private
   */
  _renderRecommendedActivityHtml() {
    if (this.recentActivityItems.length === 0) {
      return '<div class="dashboard-recent-activity__list"><div class="dashboard-recent-activity__empty-row">Nothing recommended right now.</div></div>';
    }

    // Icon map for activity types
    const iconMap = {
      'path': 'icon-compass',
      'orientation': 'icon-compass',
      'guide': 'icon-guide',
      'practice': 'icon-practices',
      'active_advice_session': 'icon-chat-bubble',
      'sharing': 'icon-partner',
      'intake_issue': 'icon-chat-bubble',
      'partner_invite': 'icon-partner',
      'partner_notes': 'icon-mail',
      'guest_conversion': 'icon-user'
    };

    // At most one row per render is featured — see FEATURED_RULES — and it is
    // hoisted so its prominence and its position agree.
    const featuredItemId = this._selectFeaturedItemId(this.recentActivityItems);
    const orderedItems = this._hoistFeaturedItem(this.recentActivityItems, featuredItemId);

    const itemsHtml = orderedItems.map(item => {
      const icon = iconMap[item.type] || 'icon-search';
      const featuredClass = item.id === featuredItemId
        ? ' dashboard-recent-activity__item--featured'
        : '';

      // A path renders its artwork disc in the icon slot — the same `.path-disc`
      // its /discovery row and the picker dropdown draw, so a path is
      // recognizable by sight across every surface. A guide renders the small
      // cover page instead (renderGuideCover — the rule guideCoverHtml applies
      // on /search and this section's own library rows):
      // the same artwork, so it says which path produced it, on an upright
      // page so it never reads as that path. Every other type renders its
      // sprite glyph; artwork-less paths fall back to the compass, guides to
      // the book. (The disc HTML-escapes the filename and the cover URL-encodes
      // it — interchangeable for the registry's plain filenames.)
      const pathImage = item.metadata?.pathMetadata?.image;
      let iconSlotHtml;
      if (item.type === 'path' && pathImage) {
        iconSlotHtml = `<div class="dashboard-recent-activity__item-icon dashboard-recent-activity__item-icon--image">
            <span class="path-disc dashboard-recent-activity__item-disc"><img src="assets/images/paths/${escapeHtml(pathImage)}" alt="" class="dashboard-recent-activity__item-icon-img"></span>
          </div>`;
      } else if (item.type === 'guide' && pathImage) {
        iconSlotHtml = `<div class="dashboard-recent-activity__item-icon">${renderGuideCover(pathImage)}</div>`;
      } else {
        iconSlotHtml = `<div class="dashboard-recent-activity__item-icon">
            <svg class="dashboard-recent-activity__item-icon-svg dashboard-recent-activity__item-icon-svg--${icon}" width="20" height="20"><use href="#${icon}"></use></svg>
          </div>`;
      }

      // Progress bar for discovery paths, in the meta container — the same
      // bar primitive the /discovery hero and path drawer draw, so a path's
      // progress is one component wherever it is shown.
      const progressHtml = item.progress !== null && item.progress !== undefined
        ? `<div class="dashboard-recent-activity__progress">
            <div class="understanding__bar-track" style="--bar-width:${item.progress}%"><div class="understanding__bar-fill"></div></div>
            <span class="dashboard-recent-activity__progress-text">${item.progress}%</span>
          </div>`
        : '';

      // Meta container: progress bar for paths, relative time for resume items,
      // and a data-driven pill for badge-bearing activity types. Rendering
      // this content here keeps it vertically centered next to the chevron.
      let metaHtml = '';
      if (progressHtml) {
        metaHtml = `<div class="dashboard-recent-activity__item-meta">${progressHtml}</div>`;
      } else if (item.type === 'active_advice_session') {
        const ago = formatTimeAgo(item.metadata?.lastActivityAt);
        if (ago) {
          metaHtml = `<div class="dashboard-recent-activity__item-meta"><span class="dashboard-recent-activity__item-time">${escapeHtml(ago)}</span></div>`;
        }
      } else if ((item.type === 'guide' || item.type === 'practice' || item.type === 'sharing' || item.type === 'partner_invite' || item.type === 'partner_notes' || item.type === 'guest_conversion' || item.type === 'intake_issue') && item.badges && item.badges.length > 0) {
        const badge = item.badges[0];
        metaHtml = `<div class="dashboard-recent-activity__item-meta"><span class="notification-label notification-label--${badge.color || 'blue'}">${escapeHtml(badge.text)}</span></div>`;
      }

      // Action label (row 1) — the verb describing what the item does. Replaces
      // the old "Path:/Insight:/Practice:" title prefixes. The featured row
      // (if any) overrides this entirely with FEATURED_LABELS — see its doc
      // comment. Everything else falls back to ACTION_LABELS, which
      // orientation has no entry in (its name leads instead) when — as an
      // unfeatured row — that case is even reachable. No tone class needed:
      // the featured card's orange emphasis is scoped in CSS off the parent
      // --featured class, not this element.
      let actionLabelHtml;
      if (item.id === featuredItemId) {
        const text = FEATURED_LABELS[item.type] || 'High Priority';
        actionLabelHtml = `<div class="dashboard-recent-activity__item-action">${escapeHtml(text)}</div>`;
      } else {
        const actionLabel = ACTION_LABELS[item.type] || null;
        actionLabelHtml = actionLabel
          ? `<div class="dashboard-recent-activity__item-action">${escapeHtml(actionLabel)}</div>`
          : '';
      }

      // Item name (row 2). For the resume item the "name" is the conversation
      // descriptor; every other type uses its title. The partner-notes intro
      // row's title carries a "(new feature)" suffix so unfeatured rows (which
      // get no row-1 label — see ACTION_LABELS above) still read as new; once
      // it wins the featured slot, FEATURED_LABELS already says "New Feature"
      // on row 1, so the suffix here would just repeat it.
      const itemName = item.type === 'active_advice_session'
        ? (item.subtitle || 'Your conversation')
        : item.id === featuredItemId && item.type === 'partner_notes'
          ? item.title.replace(' (new feature)', '')
          : item.title;
      const nameHtml = `<div class="dashboard-recent-activity__item-name">${escapeHtml(itemName)}</div>`;

      // Subtitle (row 3). Urgent cases are tested FIRST, so an urgent message
      // always supersedes a plain one on the same row and only ever one renders
      // — that ordering is the whole precedence rule. `foundation` relies on it:
      // it matches the unconditional "High Priority" branch below, so it can
      // never reach the plain branch even if it carries a subtitle.
      // Types absent from the plain branch (e.g. guide) have their subtitle
      // built and then dropped, in favour of the cleaner name-led layout.
      let subtitleHtml = '';
      if (item.type === 'path' && item.metadata?.isPartnerWaiting) {
        // isPartnerWaiting is only ever true when the partner's completion
        // blocks a shared artifact (backend partnerCompletionBlocks predicate)
        // — so the line names the blocked artifact, not the waiting partner.
        // 36 chars: inside the proven nowrap/ellipsis width budget (~36-40).
        subtitleHtml = '<div class="dashboard-recent-activity__item-subtitle dashboard-recent-activity__item-subtitle--urgent">Your shared guide is waiting on you.</div>';
      } else if (item.type === 'path' && item.metadata?.pathId === 'foundation') {
        // "High Priority –" dropped: Foundation is now featured-eligible (see
        // FEATURED_RULES) and gets that as its row-1 label, so repeating it
        // here would be redundant.
        subtitleHtml = '<div class="dashboard-recent-activity__item-subtitle dashboard-recent-activity__item-subtitle--urgent">Please complete soon.</div>';
      } else if (item.type === 'orientation') {
        const stepText = item.subtitle ? `${escapeHtml(item.subtitle)} – ` : '';
        subtitleHtml = `<div class="dashboard-recent-activity__item-subtitle dashboard-recent-activity__item-subtitle--urgent">${stepText}Please finish soon.</div>`;
      } else if ((item.type === 'guest_conversion' || item.type === 'partner_invite' || item.type === 'partner_notes' || item.type === 'sharing' || item.type === 'intake_issue' || item.type === 'path') && item.subtitle) {
        // Deliberately NOT urgent: these are standing invitations, not warnings.
        // `path` reaches here only for the pinned Foundation paths, the only
        // ones the service gives a subtitle (PATH_SUBTITLES) — every other path
        // still renders as a single name row.
        subtitleHtml = `<div class="dashboard-recent-activity__item-subtitle">${escapeHtml(item.subtitle)}</div>`;
      }

      // Chevron removed: rows either carry a hover-revealed kebab (types with
      // a MENU_CONFIG entry) or nothing at all (`guest_conversion`). The
      // wrapper div is kept either way so `arrow` stays a named grid area in
      // every grid override — at rest a
      // hidden kebab collapses the `auto` column exactly like the old chevron did.
      const menuConfig = menuConfigFor(item);
      const kebabHtml = menuConfig
        ? renderRowKebab({ items: menuConfig, dataAttrs: { 'data-item-id': item.id }, ariaLabel: 'Options' })
        : '';

      return `
        <div class="dashboard-recent-activity__item dashboard-recent-activity__item--${item.type}${featuredClass}"
             data-item-type="${item.type}"
             data-item-id="${item.id}">
          ${iconSlotHtml}
          <div class="dashboard-recent-activity__item-content">
            ${actionLabelHtml}
            ${nameHtml}
            ${subtitleHtml}
          </div>
          ${metaHtml}
          <div class="dashboard-recent-activity__item-arrow">${kebabHtml}</div>
        </div>
      `;
    }).join('');

    return `
      <div class="dashboard-recent-activity__list">
        ${itemsHtml}
      </div>
    `;
  }

  /**
   * Build the section's sticky label: a static "Recommended" for guests (no
   * account-scoped content to switch between — their content fetches are
   * auth-only and would 401), or the Recommended/Recent/Starred switcher
   * button + menu for everyone else. Extracted so devTools' featuredGallery()
   * (which renders each candidate row through the real render path
   * individually, then reassembles them, bypassing renderRecentActivity()'s
   * own innerHTML assembly) can reuse this exact markup instead of a stale
   * hand-copy.
   * @returns {string} HTML
   * @private
   */
  _renderActivityLabelHtml() {
    if (window.app?.isGuest === true) {
      return '<div class="dashboard-recent-activity__label">Recommended</div>';
    }
    return `<div class="dashboard-recent-activity__label">${this._renderActivityViewSwitcherHtml()}</div>`;
  }

  /**
   * Build the view-switcher button + its dropdown menu (hand-built markup —
   * see the dashboard-recommended-dropdown plan §2.1 for why neither
   * `renderMenuItems` nor `renderMenuSections` fits). Uses the established
   * `.options-dropdown` radio-menu classes (dropdowns.css) so it inherits the
   * same visual language as every other kebab/filter menu in the app.
   * @returns {string} HTML
   * @private
   */
  _renderActivityViewSwitcherHtml() {
    const rowsHtml = ACTIVITY_VIEWS.map(view => {
      const active = view === this.activityView;
      const checkHtml = active
        ? '<svg class="options-dropdown__item-check" viewBox="0 0 3.2123498 2.408984" aria-hidden="true"><use href="#icon-checkmark"/></svg>'
        : '';
      return `<button type="button" role="menuitemradio" aria-checked="${active}" class="options-dropdown__item" data-action="view-${view}">
        <span class="options-dropdown__item-label">${escapeHtml(ACTIVITY_VIEW_LABELS[view])}</span>${checkHtml}
      </button>`;
    }).join('');

    return `
      <div class="options-dropdown">
        <button type="button" class="dashboard-recent-activity__view-btn" data-action="activity-view-toggle" aria-haspopup="menu" aria-expanded="${this._activityViewMenuOpen}">
          ${escapeHtml(ACTIVITY_VIEW_LABELS[this.activityView])}
          <svg class="dashboard-recent-activity__view-btn-chevron" width="10" height="10" aria-hidden="true"><use href="#icon-chevron-down"/></svg>
        </button>
        <div class="options-dropdown__menu dashboard-recent-activity__view-menu" role="menu" ${this._activityViewMenuOpen ? '' : 'hidden'}>
          ${rowsHtml}
        </div>
      </div>
    `;
  }

  /**
   * Open the view-switcher menu: unhide it, sync the trigger's
   * `aria-expanded`, and register the outside-click closer. Registered
   * synchronously — `handleRecentActivityAction`'s unconditional
   * `event.stopPropagation()` (called before dispatch reaches this) keeps the
   * SAME opening click from bubbling to `document` and immediately closing
   * what it just opened (mirrors the kebab `RowMenuController` hazard).
   *
   * Also switches the menu to `position: fixed` via `positionMenuFixedBelow`
   * — same fix as the row kebab menus (`RowMenuController`'s `clipContainer`
   * option) for the same root cause: `#dashboard-recent-activity` is an
   * `overflow: auto` scrollport, which clips a short menu when the list
   * below it is too short to reach the menu's height. Fixed positioning
   * escapes that clip entirely.
   * @private
   */
  _openActivityViewMenu() {
    const menu = this.recentActivityContainer?.querySelector('.dashboard-recent-activity__view-menu');
    const trigger = this.recentActivityContainer?.querySelector('[data-action="activity-view-toggle"]');
    if (!menu || !trigger) return;
    menu.hidden = false;
    positionMenuFixedBelow(menu, trigger, 8, 'left');
    trigger.setAttribute('aria-expanded', 'true');
    this._activityViewMenuOpen = true;
    document.addEventListener('click', this._boundCloseActivityViewMenu);
    document.addEventListener('keydown', this._boundActivityViewKeydown);
    window.addEventListener('scroll', this._boundCloseActivityViewMenu, { passive: true });
    window.addEventListener('resize', this._boundCloseActivityViewMenu);
  }

  /**
   * Idempotent close for the view-switcher menu — removes both document-level
   * listeners, hides the menu, and syncs the trigger's `aria-expanded`. Safe
   * to call whether or not a menu is open; called unconditionally from the
   * top of `renderRecentActivity()` and from `returnToDashboard()`, so a
   * re-render (or a fresh dashboard visit) can never strand this menu's
   * listeners on nodes that are about to be — or already were — replaced.
   * @private
   */
  _closeActivityViewMenu() {
    document.removeEventListener('click', this._boundCloseActivityViewMenu);
    document.removeEventListener('keydown', this._boundActivityViewKeydown);
    window.removeEventListener('scroll', this._boundCloseActivityViewMenu);
    window.removeEventListener('resize', this._boundCloseActivityViewMenu);
    if (!this._activityViewMenuOpen) return;
    this._activityViewMenuOpen = false;
    const menu = this.recentActivityContainer?.querySelector('.dashboard-recent-activity__view-menu');
    const trigger = this.recentActivityContainer?.querySelector('[data-action="activity-view-toggle"]');
    if (menu) {
      menu.hidden = true;
      // Reverts to the stylesheet's default (position: absolute; right: 0;
      // top: calc(100% + 4px)) so the next open recomputes from scratch —
      // matches positionMenuFixedBelow's own re-measure-from-clean contract.
      menu.style.position = '';
      menu.style.top = '';
      menu.style.left = '';
      menu.style.right = '';
    }
    trigger?.setAttribute('aria-expanded', 'false');
  }

  /**
   * Escape closes the open view-switcher menu and restores focus to its
   * trigger — matches the established OptionsDropdown/ResultsFilterBar
   * contract. Only swallows the event while the menu is actually open, so
   * Escape still reaches the right panel and modals otherwise.
   * @param {KeyboardEvent} e
   * @private
   */
  _handleActivityViewKeydown(e) {
    if (e.key !== 'Escape' || !this._activityViewMenuOpen) return;
    const trigger = this.recentActivityContainer?.querySelector('[data-action="activity-view-toggle"]');
    e.preventDefault();
    this._closeActivityViewMenu();
    trigger?.focus();
  }

  /**
   * Handle a Recommended/Recent/Starred menu selection. Re-selecting the
   * active view is a no-op (closes the menu but preserves any expanded
   * Show-more state) — only switching to a genuinely different view resets
   * `activityVisibleCount` and (for Recent/Starred, when nothing is cached
   * yet) triggers a load, rendering a lightweight loading row while it's in
   * flight. The in-flight promise cache in `_loadLibraryItems` makes a
   * mid-load re-click harmless.
   * @param {string} view - 'recommended' | 'recent' | 'starred'
   * @private
   */
  async _handleActivityViewSelect(view) {
    this._closeActivityViewMenu();
    if (view === this.activityView) return;

    this.activityView = view;
    this.activityVisibleCount = 10;

    if (view !== 'recommended' && this.libraryItems === null) {
      this.renderRecentActivity();
      await this._loadLibraryItems();
    }
    this.renderRecentActivity();
  }

  /**
   * Build the Recent/Starred view's body: filter + sort `libraryItems` per
   * `activityView`, slice to `activityVisibleCount`, and render rows plus a
   * "Show more" footer, an empty-state row, or a load-failure row. No kebabs,
   * stars, or unread dots on these rows in v1 — they are plain navigation
   * (see plan §1.6).
   * @returns {string} HTML
   * @private
   */
  _renderLibraryRowsHtml() {
    if (this.libraryItems === null) {
      const text = this.libraryLoadFailed ? "Couldn't load your items. Try again shortly." : 'Loading…';
      return `<div class="dashboard-recent-activity__list"><div class="dashboard-recent-activity__empty-row">${escapeHtml(text)}</div></div>`;
    }

    const filtered = this._filterAndSortLibraryItems();
    const visible = filtered.slice(0, this.activityVisibleCount);

    let bodyHtml;
    if (visible.length === 0) {
      const emptyText = this.activityView === 'recent'
        ? 'No recent activity yet.'
        : 'Nothing starred yet. Star sessions, notes, guides, or experiments to collect them here.';
      bodyHtml = `<div class="dashboard-recent-activity__empty-row">${escapeHtml(emptyText)}</div>`;
    } else {
      bodyHtml = visible.map(item => this._renderLibraryRowHtml(item)).join('');
    }

    // A partial failure (some sources loaded, at least one didn't) renders
    // what loaded alongside this notice — libraryLoadFailed is only ever true
    // when NOTHING useful loaded (see _fetchLibraryItems), so it never
    // shadows real content here.
    const failureHtml = this.libraryLoadFailed
      ? '<div class="dashboard-recent-activity__empty-row">Couldn\'t load your items. Try again shortly.</div>'
      : '';

    const showMoreHtml = filtered.length > this.activityVisibleCount
      ? '<button type="button" class="dashboard-recent-activity__show-more" data-action="library-show-more">Show more</button>'
      : '';

    return `<div class="dashboard-recent-activity__list">${failureHtml}${bodyHtml}${showMoreHtml}</div>`;
  }

  /**
   * Filter + sort `libraryItems` for the active view. Both sorts use
   * `getTimestamp`, which coerces a null/invalid value to `0`.
   *  - `recent`: `isRecentsEligible` (drops sent notes, archived items,
   *    non-active practices), plus items with a zero timestamp (matches
   *    /search's null-date rule); sorted by `date` desc.
   *  - `starred`: `starred === true` only (archived + sent-note starred items
   *    included — mirrors the sidebar's starred block); sorted by
   *    `starredAt` desc, tie-broken by `date` desc, then `(type, id)` for
   *    stability.
   * @returns {Array<Object>} SearchItems, filtered + sorted
   * @private
   */
  _filterAndSortLibraryItems() {
    if (!this.libraryItems) return [];

    if (this.activityView === 'recent') {
      return this.libraryItems
        .filter(item => isRecentsEligible(item) && this.getTimestamp(item.date) !== 0)
        .sort((a, b) => this.getTimestamp(b.date) - this.getTimestamp(a.date));
    }

    return this.libraryItems
      .filter(item => item.starred === true)
      .sort((a, b) => {
        const starredDiff = this.getTimestamp(b.starredAt) - this.getTimestamp(a.starredAt);
        if (starredDiff !== 0) return starredDiff;
        const dateDiff = this.getTimestamp(b.date) - this.getTimestamp(a.date);
        if (dateDiff !== 0) return dateDiff;
        if (a.type !== b.type) return a.type < b.type ? -1 : 1;
        return String(a.id) < String(b.id) ? -1 : 1;
      });
  }

  /**
   * Render one Recent/Starred row: icon (shared `TYPE_ICON` sprite lookup —
   * sizes are dashboard.css's, not `TYPE_ICON_SIZE`'s, since these rows live
   * inside `.dashboard-recent-activity__list` alongside the Recommended rows
   * and are meant to match them) with a top-right star badge iff starred
   * (mirrors the sidebar recents' icon treatment — no unread dot on
   * dashboard rows, star badge only), title, a muted meta date (`date` for
   * Recent, `starredAt` for Starred — "when you starred it" is the Starred
   * list's organizing fact — in the same `__item-time` style the resume row's
   * "just now" uses), and a sidebar-parity hover-revealed kebab (see
   * `contentRowActions.js`'s `buildContentRowMenuItems` — the SAME per-type
   * menu items and mutation endpoints the sidebar recents rows use).
   * @param {Object} item - SearchItem
   * @returns {string} HTML
   * @private
   */
  _renderLibraryRowHtml(item) {
    const iconSlug = TYPE_ICON[item.type] || 'document';
    const starBadgeHtml = item.starred
      ? `<span class="dashboard-recent-activity__item-icon-star" role="img" aria-label="Starred">${STAR_ICON}</span>`
      : '';
    const dateValue = this.activityView === 'starred' ? item.starredAt : item.date;
    const dateHtml = `<span class="dashboard-recent-activity__item-time">${escapeHtml(this.formatActivityDate(dateValue))}</span>`;
    const menuKey = `${item.type}:${item.id}`;
    const kebabHtml = renderRowKebab({
      items: buildContentRowMenuItems(item),
      // `data-item-id` (not a bare `item.id`, which can collide across
      // types — see searchSources.js) is the SAME attribute the Recommended
      // rows' kebab carries, so the single shared `this._rowMenu`
      // (RowMenuController, `keyAttr: 'data-item-id'`) opens/closes/tracks
      // both row families without a second controller instance.
      dataAttrs: { 'data-item-id': menuKey },
      ariaLabel: item.type === 'practice' ? 'Experiment options' : 'Options'
    });

    return `
      <div class="dashboard-recent-activity__item dashboard-recent-activity__item--library"
           data-library-type="${escapeHtml(item.type)}"
           data-library-id="${escapeHtml(String(item.id))}">
        <div class="dashboard-recent-activity__item-icon">
          ${guideCoverHtml(item)
            || `<svg class="dashboard-recent-activity__item-icon-svg dashboard-recent-activity__item-icon-svg--icon-${iconSlug}" width="20" height="20"><use href="#icon-${iconSlug}"></use></svg>`}
          ${starBadgeHtml}
        </div>
        <div class="dashboard-recent-activity__item-content">
          <div class="dashboard-recent-activity__item-name">${escapeHtml(item.title || '')}</div>
        </div>
        <div class="dashboard-recent-activity__item-meta">${dateHtml}</div>
        <div class="dashboard-recent-activity__item-arrow">${kebabHtml}</div>
      </div>
    `;
  }

  /**
   * Load the Recent/Starred content set, with an in-flight-load guard and a
   * promise-identity commit gate: if `returnToDashboard()` resets
   * `_libraryLoadPromise` to `null` while THIS call is still awaiting the old
   * promise (a load started on a previous visit resolving after the user has
   * already moved on), the stale result is discarded rather than seeded into
   * the new visit's cache.
   * @returns {Promise<void>}
   * @private
   */
  async _loadLibraryItems() {
    this._libraryLoadPromise ??= this._fetchLibraryItems();
    const p = this._libraryLoadPromise;
    const result = await p;
    if (this._libraryLoadPromise !== p) return;
    this.libraryItems = result.items;
    this.libraryLoadFailed = result.failed;
    // Total failure: clear the promise cache too, so the next different-view
    // click naturally retries instead of replaying the same failed promise —
    // libraryItems staying null is what signals "not yet successfully loaded".
    if (result.items === null) this._libraryLoadPromise = null;
  }

  /**
   * Fetch + normalize every Recent/Starred source in parallel (session, note,
   * guide, practice — the same `searchSources` adapters /search uses, plus
   * the shared `PRACTICE_SOURCE`). A rejected source is logged and excluded;
   * a fulfilled one flattens into the item list even if empty.
   *
   * `failed` is computed over ATTEMPTED sources: at least one rejected AND no
   * source fulfilled with a non-empty result. A naive "all rejected" check
   * would miss the couple-gated note adapter, which *fulfills* with an empty
   * list for an uncoupled user rather than rejecting — without this, a total
   * outage for an uncoupled user (session/guide/practice all reject) would
   * masquerade as the ordinary empty state instead of surfacing as a failure.
   * @returns {Promise<{items: Array<Object>|null, failed: boolean}>} `items`
   *   is `null` only on total failure (see `_loadLibraryItems`'s cache-clear).
   * @private
   */
  async _fetchLibraryItems() {
    const ctx = { stampContinuationTitles };
    const sources = [...searchSources, PRACTICE_SOURCE];
    const results = await Promise.allSettled(sources.map(source => source.load(ctx)));

    const items = [];
    let anyFulfilledNonEmpty = false;
    let anyRejected = false;
    results.forEach((result, i) => {
      const source = sources[i];
      if (result.status === 'fulfilled') {
        const normalized = source.normalize(result.value);
        items.push(...normalized);
        if (normalized.length > 0) anyFulfilledNonEmpty = true;
      } else {
        console.error(`[Dashboard] Failed to load ${source.label}:`, result.reason);
        anyRejected = true;
      }
    });

    const failed = anyRejected && !anyFulfilledNonEmpty;
    return { items: failed ? null : items, failed };
  }

  /**
   * `content:starred` bridge listener (contentSync) — patches a matching
   * `libraryItems` entry from ANY surface's star/unstar (including this
   * dashboard's own, whose `runStar` already patched the same object by
   * reference, making this idempotent-skip a free no-op). Re-renders only
   * when a library view is showing — a Recommended-view patch is invisible
   * until the user switches, at which point `_filterAndSortLibraryItems`
   * reads the already-patched array. Mirrors
   * `SidebarRecentsController._onContentStarred` field-for-field.
   * @param {{itemType: string, itemId: string, starred: boolean}} data
   * @private
   */
  _onLibraryContentStarred({ itemType, itemId, starred } = {}) {
    const result = reduceNormalizedItems(
      this.libraryItems,
      CONTENT_STARRED,
      { itemType, itemId, starred }
    );
    if (!result.changed) return;
    this.libraryItems = result.collection;
    if (this.activityView !== 'recommended') this.renderRecentActivity();
  }

  /**
   * `content:archived` bridge listener (contentSync). Practices mirror the
   * lifecycle onto `payload.status`; every other library type (session, note,
   * guide) uses `payload.archivedAt` — threads never reach the dashboard's
   * `libraryItems` (not part of `searchSources`/`PRACTICE_SOURCE`), so unlike
   * the sidebar's version this has no `dismissedAt` branch. Mirrors
   * `SidebarRecentsController._onContentArchived` otherwise.
   * @param {{itemType: string, itemId: string, archived: boolean}} data
   * @private
   */
  _onLibraryContentArchived({ itemType, itemId, archived } = {}) {
    const result = reduceNormalizedItems(
      this.libraryItems,
      CONTENT_ARCHIVED,
      { itemType, itemId, archived }
    );
    if (!result.changed) return;
    this.libraryItems = result.collection;
    if (this.activityView !== 'recommended') this.renderRecentActivity();
  }

  /**
   * `content:deleted` bridge listener (contentSync). Removes the item from
   * `libraryItems` if present; a no-op (no re-render) if absent or not yet
   * loaded.
   * @param {{itemType: string, itemId: string}} data
   * @private
   */
  _onLibraryContentDeleted({ itemType, itemId } = {}) {
    const result = reduceNormalizedItems(
      this.libraryItems,
      CONTENT_DELETED,
      { itemType, itemId }
    );
    if (!result.changed) return;
    this.libraryItems = result.collection;
    if (this.activityView !== 'recommended') this.renderRecentActivity();
  }

  /**
   * `content:viewed` bridge listener (contentSync) — keeps a `libraryItems`
   * entry's unread state (and therefore its Mark as read/unread kebab item)
   * in sync with every surface, including this dashboard's own confirmed
   * toggles. Guides carry no `payload.viewedAt` field (`searchSources.js`'s
   * `normalizeGuide` — freshness-derived instead), mirroring
   * `SidebarRecentsController._onContentViewed`.
   * @param {{itemType: string, itemId: string, viewed: boolean}} data
   * @private
   */
  _onLibraryContentViewed({ itemType, itemId, viewed } = {}) {
    const result = reduceNormalizedItems(
      this.libraryItems,
      CONTENT_VIEWED,
      { itemType, itemId, viewed }
    );
    if (!result.changed) return;
    this.libraryItems = result.collection;
    if (this.activityView !== 'recommended') this.renderRecentActivity();
  }

  /**
   * `content:flagged` bridge listener (contentSync), session-only — keeps a
   * `libraryItems` session entry's manual unread state in sync with every
   * surface, including this dashboard's own confirmed toggle. Mirrors
   * `SidebarRecentsController._onContentFlagged`.
   * @param {{itemType: string, itemId: string, flagged: boolean}} data
   * @private
   */
  _onLibraryContentFlagged({ itemType, itemId, flagged } = {}) {
    const result = reduceNormalizedItems(
      this.libraryItems,
      CONTENT_FLAGGED,
      { itemType, itemId, flagged }
    );
    if (!result.changed) return;
    this.libraryItems = result.collection;
    if (this.activityView !== 'recommended') this.renderRecentActivity();
  }

  /**
   * Load nav card status data from BadgeService
   */
  loadNavCardStatus() {
    // Get status from BadgeService (initialized before DashboardManager)
    if (window.badgeService) {
      this.navCardStatus.discovery = window.badgeService.getDiscoveryStatus();
    }
  }

  /**
   * Render the dashboard HTML
   */
  render() {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="dashboard__content">
        <button class="dashboard__menu-btn" id="dashboard-menu-btn" aria-label="Menu">
          <svg width="30" height="30" viewBox="-1 0 30 30"><use href="#icon-menu"/></svg>
        </button>
        <button class="privacy-toggle-btn dashboard__privacy-btn" id="dashboard-privacy-btn" aria-label="Enter privacy mode">
          <svg class="icon-default" width="16" height="20"><use href="#icon-lock-unlocked"/></svg>
          <svg class="icon-hover" width="16" height="20"><use href="#icon-lock-locked"/></svg>
          <span class="header-privacy-btn__label">Private</span>
        </button>
        <div class="dashboard__logo">
          ${this._renderLogoSvg()}
        </div>

        <div class="dashboard__group">
          <div class="dashboard__greeting">${this.userName ? `Hi ${escapeHtml(this.userName)}, w` : 'W'}hat should we work on today?</div>

          <div class="dashboard__navigation">
            ${this.renderNavigationCards()}
          </div>

          ${this.suggestedPrompts.length > 0 ? `
            <div class="dashboard__suggested-prompts">
              ${this.renderSuggestedPrompts()}
            </div>
          ` : ''}
        </div>
      </div>
    `;

    // Strip element was replaced by innerHTML rewrite — re-measure and re-attach observer.
    this._updateSuggestedPromptsHeight?.();

    // The privacy button is a fresh DOM node every render() — PrivacyModeManager
    // needs an idempotent re-apply (P10 W3: a once-bound reference would die on
    // the next innerHTML rewrite).
    window.app?.modules?.privacyModeManager?.registerButton(
      this.container.querySelector('#dashboard-privacy-btn')
    );

    // #dashboard-menu-btn is a fresh DOM node every render() too — reapply the
    // aggregate badge dot for the same reason as the privacy button above.
    this.updateMenuButtonBadge();
  }

  /**
   * @returns {string} Logo SVG markup
   */
  _renderLogoSvg() {
    // Full Encouple logo (brand mark centered above the wordmark). Shared with
    // the auth/SSO screens via the #logo-encouple sprite symbol; the wordmark
    // is currentColor so it picks up .dashboard__logo's color.
    return `<svg width="130" height="55" viewBox="0 -3 143.80138 60.759407" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><use href="#logo-encouple"/></svg>`;
  }

  /**
   * Render navigation cards
   * @returns {string} HTML for navigation cards
   */
  renderNavigationCards() {
    const cards = [
      {
        id: 'discover',
        title: 'Discovery',
        description: 'Answer questions to build and deepen self-understanding.',
        mode: 'DISCOVERY',
        statusKey: 'discovery'
      },
      {
        id: 'advice',
        title: 'Advice',
        description: 'Discuss anything and receive personalized guidance.',
        mode: 'ADVICE',
        statusKey: 'advice'
      }
    ];

    return cards.map(card => {
      const status = this.navCardStatus[card.statusKey] || {};
      const discoveryTagHtml = card.statusKey === 'discovery' && status.tag
        ? `<span class="notification-pill notification-pill--${status.tag.color}">${status.tag.text}</span>`
        : '';
      const statusHtml = status.statusText
        ? `<div class="dashboard__nav-card-status">${status.statusText}</div>`
        : '';

      return `
        <div class="card dashboard__nav-card" data-mode="${card.mode}" data-card-id="${card.id}">
            <div class="dashboard__nav-card-header">
              <div class="dashboard__nav-card-title">${card.title}${discoveryTagHtml}</div>
              <div class="dashboard__nav-card-meta">
                <span class="dashboard__nav-card-indicator"></span>
                <div class="dashboard__nav-card-icon">
                  <svg width="18" height="22" viewBox="0 -3 20 20" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              </div>
            </div>
            <div class="dashboard__nav-card-description">${card.description}</div>
            ${statusHtml}
        </div>
      `;
    }).join('');
  }

  /**
   * Render suggested prompt buttons. Picker buttons get a count pill that is
   * populated after-the-fact by `updateSuggestedPromptsState` once prefetch
   * lands; non-picker buttons (e.g. "I'm feeling...") omit the pill entirely.
   * @returns {string} HTML for prompt buttons
   */
  renderSuggestedPrompts() {
    return this.suggestedPrompts.map(prompt => {
      const count = this.dashboardPickerCounts[prompt.pickerType];
      // Picker buttons always show the count pill (even at 0) once prefetch lands;
      // before prefetch the count is undefined and the pill renders empty.
      const hasCount = typeof count === 'number';
      const countHtml = prompt.pickerType
        ? `<span class="btn--suggestion__count">${hasCount ? count : ''}</span>`
        : '';
      return `
      <button
        type="button"
        class="btn--suggestion"
        data-prompt-id="${prompt.id}"
        data-prompt-text="${prompt.promptText ? escapeHtml(prompt.promptText) : ''}"
        data-picker-type="${prompt.pickerType || ''}"
        data-prompt-mode="${prompt.mode || 'ADVICE'}"
      >
        <span class="btn--suggestion__label">${escapeHtml(prompt.displayText)}</span>
        ${countHtml}
      </button>
    `;
    }).join('');
  }

  /**
   * Update nav card badges without full re-render
   * Called when BadgeService state changes
   */
  updateNavCardBadges() {
    if (!this.container) return;

    const navCards = this.container.querySelectorAll('.dashboard__nav-card');
    navCards.forEach(card => {
      const cardId = card.dataset.cardId;
      const statusKey = cardId === 'discover' ? 'discovery' : cardId;
      const status = this.navCardStatus[statusKey] || {};

      // Update tag
      const titleEl = card.querySelector('.dashboard__nav-card-title');
      titleEl?.querySelector('.notification-pill')?.remove();

      if (status.tag && statusKey === 'discovery' && titleEl) {
        const tagEl = document.createElement('span');
        tagEl.className = `notification-pill notification-pill--${status.tag.color}`;
        tagEl.textContent = status.tag.text;
        titleEl.appendChild(tagEl);
      }

      // Update status section
      // Remove existing status
      const existingStatus = card.querySelector('.dashboard__nav-card-status');
      if (existingStatus) existingStatus.remove();

      // Add new status if exists
      if (status.statusText) {
        const statusEl = document.createElement('div');
        statusEl.className = 'dashboard__nav-card-status';
        statusEl.textContent = status.statusText;
        card.appendChild(statusEl);
      }
    });
  }

  /**
   * Setup event listeners for dashboard interactions.
   *
   * All handlers are delegated from the two persistent containers
   * (`this.container`, `this.recentActivityContainer`) whose lifetime spans
   * every re-render. Binding is therefore idempotent — this method is a no-op
   * on subsequent calls so listeners cannot accumulate across dashboard visits.
   */
  setupEventListeners() {
    if (!this.container || this._listenersAttached) return;
    this._listenersAttached = true;

    // Listen for badge state changes (BadgeService loads async after dashboard)
    this.eventBus.on('badge:state-changed', () => {
      this.loadNavCardStatus();
      this.updateNavCardBadges();
      this.updateSuggestedPromptsState();
      this.updateMenuButtonBadge();
    });

    // Repopulate recent activity once orientation completes — the Foundation
    // path filter in `loadRecentActivity` flips off at that point.
    this.eventBus.on('orientation:completed', () => this.refreshRecentActivity());

    this.eventBus.on('autocomplete:hidden', () => {
      this.clearActiveShortcutPrompt();
      this.activeDashboardPromptId = null;
      this._setActiveSuggestionButton(null);
    });

    // Universal cross-surface sync bridge (contentSync) — a star/archive/
    // delete/mark-viewed/flag from ANY surface (sidebar recents, /search,
    // /notes, /practices, /discovery's Open Threads, the /insights guides
    // tab, or this dashboard's own library-row kebabs) live-patches a
    // matching `libraryItems` entry. Patch-only — these handlers never
    // re-emit, so the bridge can't loop. Mirrors
    // `SidebarRecentsController`'s `_onContent*` listeners field-for-field.
    this.eventBus.on(CONTENT_STARRED, (data) => this._onLibraryContentStarred(data));
    this.eventBus.on(CONTENT_ARCHIVED, (data) => this._onLibraryContentArchived(data));
    this.eventBus.on(CONTENT_DELETED, (data) => this._onLibraryContentDeleted(data));
    this.eventBus.on(CONTENT_VIEWED, (data) => this._onLibraryContentViewed(data));
    this.eventBus.on(CONTENT_FLAGGED, (data) => this._onLibraryContentFlagged(data));

    // All container-scoped clicks (menu btn, CTA btn, nav cards, prompts)
    // handled via delegation — survives innerHTML replacement during render().
    this.container.addEventListener('click', (e) => {
      if (e.target.closest('#dashboard-menu-btn')) {
        this.eventBus?.emit('sidebar:toggle');
        return;
      }

      if (e.target.closest('#dashboard-privacy-btn')) {
        // Dashboard privacy button ARMS the next session for privacy (no API
        // call yet — the session doesn't exist until the user submits). Same
        // toggle both ways: click again to disarm.
        window.app?.modules?.privacyModeManager?.toggleArm();
        return;
      }

      const navCard = e.target.closest('.dashboard__nav-card');
      if (navCard) {
        this.handleNavigationCardClick(navCard.dataset.mode, navCard.dataset.cardId);
        return;
      }

      const promptButton = e.target.closest('.btn--suggestion');
      if (promptButton) {
        e.preventDefault();
        e.stopPropagation();
        this.handlePromptClick(
          promptButton.dataset.promptId,
          promptButton.dataset.promptText,
          promptButton.dataset.promptMode || 'ADVICE',
          promptButton.dataset.pickerType
        );
      }
    });

    const autocompleteList = document.getElementById('input-autocomplete-list');
    if (autocompleteList) {
      autocompleteList.addEventListener('click', (e) => {
        if (e.target.closest('.dashboard-picker__close')) {
          e.preventDefault();
          e.stopPropagation();
          const input = window.app?.modules?.input;
          if (input) input.hideAutocomplete();
          return;
        }
        const addBtn = e.target.closest('[data-picker-add]');
        if (addBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.handlePickerAddClick(addBtn.dataset.pickerAdd);
          return;
        }
        const showAll = e.target.closest('[data-picker-show-all]');
        if (showAll) {
          e.preventDefault();
          e.stopPropagation();
          this.handlePickerShowAllClick(showAll.dataset.pickerShowAll);
          return;
        }
        const pickerItem = e.target.closest('.dashboard-picker__item');
        if (!pickerItem) return;
        e.preventDefault();
        e.stopPropagation();
        this.handleDashboardPickerItemClick(pickerItem.dataset.pickerItemId);
      });
    }

    // Recent Activity item clicks (separate persistent container). Kebab
    // toggle + menu-item clicks are handled by the delegated data-action
    // switch below; whole-row navigation bails on those via the house guard
    // pattern (closest('[data-action]') || closest('.note-row__actions')) —
    // ratified decision §1.4 governs THIS bail (row navigation), not the
    // per-action stopPropagation() calls inside handleRecentActivityAction
    // (a different path, guarding the menu's own document-click-close listener).
    if (this.recentActivityContainer) {
      this.recentActivityContainer.addEventListener('click', (e) => {
        const target = e.target.closest('[data-action]');
        if (target) {
          this.handleRecentActivityAction(e, target);
          return;
        }
        if (e.target.closest('.note-row__actions')) return;

        // Recent/Starred library rows — resolved BEFORE the recommended-row
        // lookup below via a structurally distinct attribute (data-library-id,
        // not data-item-id), so the two lookups can never cross-match.
        const libraryRow = e.target.closest('[data-library-id]');
        if (libraryRow) {
          const item = this.libraryItems?.find(i =>
            i.type === libraryRow.dataset.libraryType && String(i.id) === libraryRow.dataset.libraryId
          );
          if (item) openContentItem(item);
          return;
        }

        const activityItem = e.target.closest('.dashboard-recent-activity__item');
        if (activityItem) {
          const item = this.recentActivityItems.find(i => i.id === activityItem.dataset.itemId);
          if (item) {
            this.handleActivityItemClick(item);
          }
        }
      });

      // Right-click a row to open its kebab menu (house parity with Sessions/Practices).
      this.recentActivityContainer.addEventListener('contextmenu', (e) => {
        this._rowMenu.handleContextMenu(e);
      });
    }
  }

  /**
   * Delegated `data-action` dispatch for the recent-activity kebab + its menu
   * items. Every case calls `event.stopPropagation()` first — `toggle()`
   * registers a document click-close listener synchronously, and without this
   * the SAME bubbling click would close the menu it just opened (the
   * stopPropagation regression guard all six existing RowMenuController
   * consumers rely on, e.g. SessionsSection.js).
   * @param {MouseEvent} event
   * @param {HTMLElement} target - The closest `[data-action]` element
   */
  handleRecentActivityAction(event, target) {
    event.stopPropagation();
    const action = target.getAttribute('data-action');

    // View-switcher + Show-more actions carry no data-item-id, so they must
    // be handled — and return — BEFORE the recentActivityItems.find() lookup
    // below, which otherwise `return`s on `!item` and silently swallows every
    // one of these clicks (dashboard-recommended-dropdown plan §3.5a).
    if (action === 'activity-view-toggle') {
      if (this._activityViewMenuOpen) {
        this._closeActivityViewMenu();
      } else {
        this._openActivityViewMenu();
      }
      return;
    }
    if (action === 'view-recommended' || action === 'view-recent' || action === 'view-starred') {
      this._handleActivityViewSelect(action.slice('view-'.length));
      return;
    }
    if (action === 'library-show-more') {
      this.activityVisibleCount += 10;
      this.renderRecentActivity();
      return;
    }

    // Recent/Starred library rows — resolved BEFORE the recommended-row
    // kebab handling and its `recentActivityItems.find()` lookup below, via a
    // structurally distinct attribute (data-library-id, never present on a
    // recommended row) so the two lookups can never cross-match. This order
    // is load-bearing (dashboard-recommended-dropdown plan §3.5a): a library
    // action must never fall through to the recommended lookup, which
    // `return`s on `!item` and would silently swallow it.
    const libRow = target.closest('[data-library-id]');
    if (libRow) {
      if (action === 'row-options') {
        this._rowMenu.toggle(target);
        return;
      }
      this._rowMenu.close();
      const item = this.libraryItems?.find(i =>
        i.type === libRow.dataset.libraryType && String(i.id) === libRow.dataset.libraryId
      );
      if (item) this._runLibraryRowAction(action, item);
      return;
    }

    if (action === 'row-options') {
      this._rowMenu.toggle(target);
      return;
    }

    this._rowMenu.close();

    const itemId = target.getAttribute('data-item-id');
    const item = this.recentActivityItems.find(i => i.id === itemId);
    if (!item) return;

    switch (action) {
      case 'row-open':
        this.handleActivityItemClick(item);
        break;
      case 'row-dismiss':
        this._dismissActivityItem(item);
        break;
      case 'row-view-practice':
        this._viewPracticeFromActivity(item);
        break;
      case 'row-archive-practice':
        this._archivePracticeFromActivity(item);
        break;
      case 'row-end_session':
        this._endAdviceSessionFromActivity(item);
        break;
      default:
        break;
    }
  }

  /**
   * Run a Recent/Starred library-row kebab action, guarded by a single
   * in-flight flag (mirrors `SidebarRecentsController._runRowAction`) so a
   * rapid second click before the first call resolves can't double-fire.
   * Every mutation routes through the shared `contentRowActions.js` runners —
   * the SAME ones the sidebar recents rows use — which patch the item (a
   * `libraryItems` entry, by reference) and broadcast on verified success
   * only. Delete confirms first via `confirmDeleteContentItem` and re-checks
   * the item's presence after the confirm await — a live content-sync event
   * could have already removed it while the modal was open.
   * @param {'row-star'|'row-discuss'|'row-view'|'row-flag'|'row-archive'|'row-restore'|'row-delete'} action
   * @param {Object} item - SearchItem, resolved from `this.libraryItems`
   * @returns {Promise<void>}
   * @private
   */
  async _runLibraryRowAction(action, item) {
    if (this._libraryActionInFlight) return;
    this._libraryActionInFlight = true;
    try {
      switch (action) {
        case 'row-star':
          await runStar(item);
          break;
        case 'row-discuss':
          await discussContentItem(item);
          break;
        case 'row-view':
          await runMarkViewed(item, !item.viewed);
          break;
        case 'row-flag':
          await runToggleFlag(item, !item.flagged);
          break;
        case 'row-archive':
        case 'row-restore':
          await runArchive(item);
          break;
        case 'row-delete': {
          const confirmed = await confirmDeleteContentItem(item);
          if (!confirmed) return;
          const stillPresent = this.libraryItems?.some(i => i.type === item.type && String(i.id) === String(item.id));
          if (!stillPresent) return;
          // Runner emits content:deleted on success; removal from THIS
          // surface's own libraryItems array stays here (a shared runner has
          // no access to it — see contentRowActions.js's module doc).
          if (await runDelete(item)) {
            this.libraryItems = this.libraryItems.filter(i => !(i.type === item.type && String(i.id) === String(item.id)));
          }
          break;
        }
        default:
          return;
      }
    } finally {
      this._libraryActionInFlight = false;
    }
    this.renderRecentActivity();
  }

  /**
   * "Dismiss" kebab action — a dashboard-only hide, never a real mutation.
   * Optimistic: removes the row immediately, then persists; restores it and
   * shows an error toast on failure (transparent-errors rule).
   *
   * Demo-mode rows (dev.dashboard.demo()/feature()/featuredGallery()) short-
   * circuit to a local-only removal — their ids are mocks the backend would
   * 400 on. Guests dismissing `orientation` (the only type reachable
   * pre-account — loadRecentActivity skips their backend fetch entirely) are
   * also local-only, via a localStorage window instead of the POST.
   * @param {Object} item
   * @private
   */
  async _dismissActivityItem(item) {
    const index = this.recentActivityItems.findIndex(i => i.id === item.id);
    if (index === -1) return;
    const [removed] = this.recentActivityItems.splice(index, 1);
    this.renderRecentActivity();

    if (this._demoMode) {
      console.log('[Dashboard] Demo mode — dismiss applied locally only (no POST):', item.id);
      return;
    }

    if (window.app?.isGuest === true && item.type === 'orientation') {
      this._dismissGuestOrientation();
      return;
    }

    try {
      const response = await fetchWithTimeout(`${API_URL}/dashboard/recent-activity/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ itemId: item.id, stateKey: item.stateKey ?? null })
      });
      const result = response.ok ? await response.json() : null;
      if (!result?.success) {
        throw new Error(`Dismiss failed: ${response.status}`);
      }
    } catch (error) {
      console.error('[Dashboard] Failed to dismiss activity item:', item.id, error);
      this.recentActivityItems.splice(index, 0, removed);
      this.renderRecentActivity();
      window.notificationManager?.showError("Couldn't dismiss that right now.");
    }
  }

  /**
   * Guest orientation dismiss — no server DashboardDismissal row exists
   * pre-account, so persistence is a 24h localStorage window (guests are
   * single-device, no cross-device concern). Wrapped in try/catch: private
   * browsing can throw on `setItem`, and an unguarded throw here would abort
   * the click handler mid-way (house precedent: Sidebar.js).
   * @private
   */
  _dismissGuestOrientation() {
    try {
      const snoozeUntil = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      localStorage.setItem(GUEST_ORIENTATION_DISMISS_KEY, snoozeUntil);
    } catch (error) {
      console.error('[Dashboard] Could not persist guest orientation dismissal:', error);
    }
  }

  /**
   * "View experiment" kebab action — opens the practice's detail view.
   * @param {Object} item
   * @private
   */
  _viewPracticeFromActivity(item) {
    const practiceNumber = item.metadata?.practiceNumber;
    if (practiceNumber == null) return;
    page(`/experiments?practice=${practiceNumber}`);
  }

  /**
   * "Archive" kebab action on a due-practice row. Removes the row on success
   * and syncs the change to every other surface (Practices list, sidebar
   * badges) via the shared contentSync events + standard toast copy —
   * mirrors PracticesSection's own archive call site.
   * @param {Object} item
   * @private
   */
  async _archivePracticeFromActivity(item) {
    const practiceId = item.metadata?.practiceId;
    if (!practiceId) return;

    if (this._demoMode) {
      console.log('[Dashboard] Demo mode — archive applied locally only (no API call):', item.id);
      this._removeActivityItem(item.id);
      return;
    }

    try {
      await API.archivePractice(practiceId);
      this._removeActivityItem(item.id);
      emitContentArchived('practice', practiceId, true);
      notifyArchived({ count: 1, type: 'practice', archived: true });
    } catch (error) {
      console.error('[Dashboard] Failed to archive practice from activity row:', practiceId, error);
      notifyArchiveFailed({ count: 1, type: 'practice', archived: true });
    }
  }

  /**
   * "End session" kebab action on the resume-your-conversation row. Confirms,
   * closes the session server-side, then removes the row — treating an
   * already-closed session (`SESSION_NOT_ACTIVE`, e.g. closed in another tab)
   * as success, mirroring the resume-path precedent in app.js. Uses
   * `API.closeSession` directly (NOT `sessionManager.endSession()`, which acts
   * on whatever session is currently loaded into chat — irrelevant here since
   * the user is on the dashboard, not necessarily inside this row's session).
   * @param {Object} item
   * @private
   */
  async _endAdviceSessionFromActivity(item) {
    const sessionId = item.metadata?.sessionId;
    if (!sessionId) return;

    const confirmed = await modalService.confirm({
      title: 'End this conversation?',
      message: "You won't be able to resume it.",
      confirmText: 'End Session',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    if (this._demoMode) {
      console.log('[Dashboard] Demo mode — end session applied locally only (no API call):', item.id);
      this._removeActivityItem(item.id);
      return;
    }

    const result = await API.closeSession(sessionId);
    const alreadyClosed = result?.code === 'SESSION_NOT_ACTIVE';
    if (result?.closed !== true && !alreadyClosed) {
      window.notificationManager?.showError("Couldn't end this session — please try again.");
      return;
    }

    this._removeActivityItem(item.id);
    // Otherwise the nav-card restore path resurrects a session that was just closed.
    if (window.app?.returnSession?.sessionId === sessionId) {
      window.app.returnSession = null;
    }
  }

  /**
   * Remove one item from the recent-activity list and re-render.
   * @param {string} id
   * @private
   */
  _removeActivityItem(id) {
    const index = this.recentActivityItems.findIndex(i => i.id === id);
    if (index === -1) return;
    this.recentActivityItems.splice(index, 1);
    this.renderRecentActivity();
  }

  /**
   * Keep the mobile suggested prompt bar attached to the top of the fixed chat input.
   * The chat input grows as the textarea expands, so CSS needs the live container height
   * instead of a fixed bottom offset.
   */
  setupPromptOffsetTracking() {
    const chatInput = document.querySelector('.chat-input');
    if (!chatInput || this.promptOffsetObserver) return;

    this._updatePromptOffset = () => {
      const height = Math.ceil(chatInput.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty('--dashboard-chat-input-height', `${height}px`);
      }
    };

    this._updatePromptOffset();

    if ('ResizeObserver' in window) {
      this.promptOffsetObserver = new ResizeObserver(this._updatePromptOffset);
      this.promptOffsetObserver.observe(chatInput);
    }

    window.addEventListener('resize', this._updatePromptOffset);

    // Track the suggested-prompts strip height so the mobile autocomplete can
    // clear it (the strip is position:fixed above the input on mobile).
    this._updateSuggestedPromptsHeight = () => {
      const strip = this.container?.querySelector('.dashboard__suggested-prompts');
      if (!strip) return;
      const height = Math.ceil(strip.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty('--dashboard-suggested-prompts-height', `${height}px`);
      }
      // Re-attach observer if the strip was replaced by a re-render.
      if ('ResizeObserver' in window && this._observedPromptsStrip !== strip) {
        this.suggestedPromptsObserver?.disconnect();
        this.suggestedPromptsObserver = new ResizeObserver(this._updateSuggestedPromptsHeight);
        this.suggestedPromptsObserver.observe(strip);
        this._observedPromptsStrip = strip;
      }
    };
    this._updateSuggestedPromptsHeight();
    // Re-measure after the browser has settled layout (handles font-load shifts).
    requestAnimationFrame(() => this._updateSuggestedPromptsHeight());
    window.addEventListener('resize', this._updateSuggestedPromptsHeight);
  }

  /**
   * Handle activity item click
   * Emits event for navigation based on item type
   * @param {Object} item - Activity item data
   */
  handleActivityItemClick(item) {
    console.log('🎯 DashboardManager: Activity item clicked:', item);
    this.eventBus.emit('dashboard:activity-selected', { item });
  }

  /**
   * Handle navigation card click with smart session restoration
   * @param {string} mode - Target mode (DISCOVERY, ADVICE)
   * @param {string} cardId - Card identifier
   */
  async handleNavigationCardClick(mode, cardId) {
    // DISCOVERY card: always fetch the next best path/topic entry (skip restoration).
    if (mode === 'DISCOVERY') {
      await this.handleDiscoveryCardClick();
      return;
    }

    // ADVICE: Check for session restoration
    if (window.app.returnSession?.mode === mode) {
      // Same mode - restore existing session
      console.log('[Dashboard] Restoring existing session for mode:', mode);
      // Navigate to chat, restoring existing session
      eventBus.emit('navigate', { page: 'chat', mode, restoreSession: true });
    } else {
      // Different mode or no return session - create new session
      console.log('[Dashboard] Creating new session for mode:', mode);
      eventBus.emit('navigate', { page: 'chat', mode });
    }
  }

  /**
   * Handle DISCOVERY card click
   * Enters guided discovery without a preselected path.
   */
  async handleDiscoveryCardClick() {
    // Guided mode — ranking engine selects the subcategory
    eventBus.emit('navigate', {
      page: 'chat',
      mode: 'DISCOVERY',
      pathId: null
    });
  }

  /**
   * Handle suggested prompt button click
   * Populates chat input with trigger text to show curated autocomplete options
   * User stays on dashboard - navigation happens when they select an autocomplete option
   * @param {string} promptId - Prompt identifier
   * @param {string} promptText - Text to populate in chat input
   * @param {string} mode - Session mode (ADVICE, etc.)
   */
  async handlePromptClick(promptId, promptText, mode, pickerType = '') {
    const input = window.app?.modules?.input;
    if (this.isDashboardDropdownOpen() && this.activeDashboardPromptId === promptId) {
      this.clearActiveShortcutPrompt();
      input?.hideAutocomplete();
      this.clearDashboardPickerState();
      this.activeDashboardPromptId = null;
      this._setActiveSuggestionButton(null);
      return;
    }

    if (pickerType) {
      this.clearActiveShortcutPrompt();
      await this.showDashboardPicker(pickerType, promptId, mode);
      return;
    }

    this.clearDashboardPickerState();
    if (!promptText) {
      console.log('[Dashboard] Suggested prompt is not wired yet:', promptId);
      return;
    } else {
      // Populate input with trigger text to show curated autocomplete options
      // Stay on dashboard - don't navigate yet
      console.log('[Dashboard] Populating input with shortcut:', promptText);
      const targetMode = mode || 'ADVICE';
      if (input) {
        // Set pending mode so it's used when user selects autocomplete option
        input.setPendingMode(targetMode);
        input.populateAndShowAutocomplete(promptText);
        this.activeDashboardPromptId = promptId;
        this.activeShortcutPromptText = promptText;
        this._setActiveSuggestionButton(promptId);
      }
    }
  }

  /**
   * Render one of the dashboard prompt pickers into the shared chat input dropdown.
   * @param {string} pickerType - discovery | practices
   * @param {string} promptId - Prompt identifier
   * @param {string} mode - Pending mode for chat actions
   */
  async showDashboardPicker(pickerType, promptId, mode) {
    const input = window.app?.modules?.input;
    if (!input?.autocompleteList || !input?.autocompleteContainer) return;

    // Only seed pendingMode when the picker entry is explicitly tied to a chat
    // mode (e.g. an "I'm feeling..." style shortcut). Discovery /
    // Practices pickers navigate elsewhere — leaving pendingMode untouched here
    // prevents a stale 'ADVICE' from surviving into a later L2 reply and
    // hijacking the active session.
    const targetMode = mode || null;
    input.cancelPendingAutocompleteHide?.();
    if (targetMode) {
      input.setPendingMode(targetMode);
    }
    // Anchor the picker dropdown to the input. On touch devices this avoids
    // focusing the textarea (which would open the soft keyboard) while still
    // wiring up click-away dismissal — see Input.focusForPicker().
    input.focusForPicker();

    input.autocompleteContainer.classList.add('chat-input__autocomplete--dashboard-picker');
    input.autocompleteList.innerHTML = `
      <div class="dashboard-picker__loading" role="status">Loading...</div>
    `;
    input.showAutocomplete();
    this.activeDashboardPromptId = promptId;
    this._setActiveSuggestionButton(promptId);

    try {
      const items = await this.loadDashboardPickerItems(pickerType);
      this.dashboardPickerItems = items;
      input.autocompleteList.innerHTML = this.renderDashboardPickerItems(items, pickerType);
    } catch (error) {
      console.error('[Dashboard] Failed to load dashboard picker:', promptId, error);
      input.autocompleteList.innerHTML = `
        <div class="dashboard-picker__empty">Could not load items. Please try again.</div>
      `;
    }
  }

  /**
   * Handle a click on the "+" button in a picker header.
   * Closes the picker, then launches the same flow as the
   * "Add an experiment" button on the dedicated page.
   * @param {string} pickerType - 'practices'
   */
  async handlePickerAddClick(pickerType) {
    const input = window.app?.modules?.input;
    if (input) input.hideAutocomplete();

    if (pickerType === 'discovery') {
      this.handleDiscoveryCardClick();
      return;
    }

    const orchestrator = window.app?.modules?.modeOrchestrator;
    if (!orchestrator) {
      console.error('[Dashboard] No orchestrator available; cannot launch add flow');
      return;
    }

    const swapped = await window.app?.modules?.pageManager?.showPage('chat');
    if (swapped === false) return;
    window.history.pushState({}, '', '/chat');

    try {
      if (pickerType === 'practices') {
        await orchestrator.switchMode('TREATMENT', {
          subMode: 'build',
          skipWelcome: true
        });
        await orchestrator.handleUserMessage("Let's build an experiment", {
          metadata: { source: 'add_practice_button' }
        });
      }
    } catch (err) {
      console.error('[Dashboard] Failed to launch add flow for picker:', pickerType, err);
      window.notificationManager?.showError('Could not open the conversation. Please try again.');
    }
  }

  /**
   * Remove picker-specific styling from the shared autocomplete container.
   */
  clearDashboardPickerState() {
    const input = window.app?.modules?.input;
    input?.autocompleteContainer?.classList.remove('chat-input__autocomplete--dashboard-picker');
  }

  /**
   * Mark a single suggestion button as the active (open-dropdown) one.
   * Pass `null` to clear all.
   * @param {string|null} promptId
   */
  _setActiveSuggestionButton(promptId) {
    const buttons = this.container?.querySelectorAll('.btn--suggestion');
    buttons?.forEach((btn) => {
      const isActive = promptId != null && btn.dataset.promptId === promptId;
      btn.classList.toggle('btn--suggestion--active', isActive);
    });
  }

  /**
   * Clear a shortcut trigger from the input if it is still the active temporary value.
   */
  clearActiveShortcutPrompt() {
    if (!this.activeShortcutPromptText) return;
    const input = window.app?.modules?.input;
    if (input?.getValue?.() === this.activeShortcutPromptText) {
      input.clear();
    }
    this.activeShortcutPromptText = null;
  }

  /**
   * @returns {boolean} True when the shared dashboard/autocomplete dropdown is visibly open.
   */
  isDashboardDropdownOpen() {
    const input = window.app?.modules?.input;
    return Boolean(
      input?.autocompleteContainer &&
      input.autocompleteVisible &&
      !input.autocompleteContainer.hasAttribute('hidden')
    );
  }

  /**
   * @param {string} pickerType
   * @returns {Promise<Array<Object>>}
   */
  async loadDashboardPickerItems(pickerType) {
    if (pickerType === 'discovery') return this.loadDiscoveryPromptItems();
    if (pickerType === 'practices') return this.loadPracticePromptItems();
    return [];
  }

  /**
   * Pre-fetch the picker datasets so the suggested-prompt buttons can
   * display item counts + badges and the dropdown opens instantly. Fire-and-
   * forget — updates buttons in place once data lands.
   * @returns {Promise<void>}
   */
  async loadDashboardPickerCounts() {
    try {
      await Promise.all([
        this.loadDiscoveryPromptItems(),
        this.loadPracticePromptItems()
      ]);
    } catch (error) {
      console.error('[Dashboard] Picker prefetch failed:', error);
    }
    this.updateSuggestedPromptsState();
  }

  /**
   * Update count pills and badge dots on the suggested-prompt buttons without
   * re-rendering the dashboard. Called after picker prefetch and on any
   * BadgeService state change.
   */
  updateSuggestedPromptsState() {
    if (!this.container) return;
    const buttons = this.container.querySelectorAll('.btn--suggestion');
    buttons.forEach(button => {
      const promptId = button.dataset.promptId;
      const prompt = this.suggestedPrompts.find(p => p.id === promptId);
      if (!prompt) return;

      const countEl = button.querySelector('.btn--suggestion__count');
      if (countEl) {
        const count = this.dashboardPickerCounts[prompt.pickerType];
        if (typeof count === 'number') {
          countEl.textContent = String(count);
          countEl.hidden = false;
        } else {
          countEl.textContent = '';
          countEl.hidden = true;
        }
      }

      // Badge dot sits on the button itself, independent of the count pill.
      const existingDot = button.querySelector('.notification-dot');
      if (existingDot) existingDot.remove();
      if (prompt.section) {
        const indicator = window.badgeService?.getSidebarIndicator?.(prompt.section);
        if (indicator) {
          const dot = document.createElement('span');
          dot.className = `notification-dot notification-dot--${indicator.color}`;
          dot.setAttribute('aria-hidden', 'true');
          button.appendChild(dot);
        }
      }
    });

  }

  /**
   * Apply the rolled-up sidebar badge indicator (highest priority across all
   * sections) as a single dot on the mobile hamburger menu button, so a
   * collapsed sidebar's notifications are still visible before opening it.
   */
  updateMenuButtonBadge() {
    const button = this.container?.querySelector('#dashboard-menu-btn');
    if (!button) return;

    const existingDot = button.querySelector('.notification-dot');
    if (existingDot) existingDot.remove();

    const indicator = window.badgeService?.getAggregateIndicator?.();
    if (!indicator) return;

    const dot = document.createElement('span');
    dot.className = `notification-dot notification-dot--${indicator.color}`;
    dot.setAttribute('aria-hidden', 'true');
    button.appendChild(dot);
  }

  /**
   * @returns {Promise<Array<Object>>}
   */
  async loadDiscoveryPromptItems() {
    if (this.discoveryPromptItems) return this.discoveryPromptItems;
    const result = this.coupleId
      ? await this.discoveryAPI.getPathsWithCoupleProgress(this.coupleId)
      : await this.discoveryAPI.getPathsWithUserProgress();
    const paths = result?.data?.paths || [];
    const accessiblePaths = paths.filter(path => (path.phaseAccess ?? true) && !path.isCompleted);
    this.dashboardPickerCounts.discovery = accessiblePaths.length;
    const userProgressFor = (path) => path.userProgress
      || path.partnerProgress?.find(p => p.userId === this.userId)
      || path.partnerProgress?.[0]
      || {};
    const discoveryBadgeRanks = new Map(accessiblePaths.map(path => [
      path.id,
      window.badgeService?.getCardState('discovery', path.id)?.indicator ? 1 : 0
    ]));
    const hasDiscoveryBadge = (path) => discoveryBadgeRanks.get(path.id) || 0;
    this.discoveryPromptItems = accessiblePaths
      .sort((a, b) => {
        const badgeDiff = hasDiscoveryBadge(b) - hasDiscoveryBadge(a);
        if (badgeDiff !== 0) return badgeDiff;
        const aProgress = userProgressFor(a);
        const bProgress = userProgressFor(b);
        return this.getTimestamp(bProgress.lastActivityDate) - this.getTimestamp(aProgress.lastActivityDate);
      })
      .slice(0, 5)
      .map(path => {
        const progress = userProgressFor(path);
        return {
          id: path.id,
          type: 'discovery',
          icon: 'icon-compass',
          title: path.name || 'Discovery path',
          progress: progress.percentComplete || 0,
          raw: path
        };
      });
    return this.discoveryPromptItems;
  }

  /**
   * @returns {Promise<Array<Object>>}
   */
  async loadPracticePromptItems() {
    if (this.practicePromptItems) return this.practicePromptItems;
    // limit raised so the count pill reflects all active practices, not just the first 20
    const practices = await API.getPractices({ status: 'active', limit: 200 });
    const activePractices = Array.isArray(practices) ? practices : [];
    this.dashboardPickerCounts.practices = activePractices.length;
    // Recency only: experiments carry no attention dot (BadgeService never
    // writes a `practices` card indicator — see `loadPracticesStatus`), so
    // there is no badge rank to sort ahead of the timestamp.
    this.practicePromptItems = activePractices
      .sort((a, b) => this.getTimestamp(b.updatedAt || b.createdAt) - this.getTimestamp(a.updatedAt || a.createdAt))
      .slice(0, 5)
      .map(practice => {
        const title = practice.title || practice.definition?.name || 'Experiment';
        return {
          id: practice.id,
          type: 'practice',
          icon: 'icon-practices',
          title,
          meta: this.formatActivityDate(practice.updatedAt || practice.createdAt),
          raw: practice
        };
      });
    return this.practicePromptItems;
  }

  /**
   * @param {Array<Object>} items
   * @param {string} pickerType
   * @returns {string}
   */
  renderDashboardPickerItems(items, pickerType) {
    const titleLabels = {
      discovery: 'Discovery Paths',
      practices: 'Experiments'
    };
    const headerHtml = `
      <div class="dashboard-picker__header">
        <span class="dashboard-picker__title">${escapeHtml(titleLabels[pickerType] || '')}</span>
        <button type="button" class="dashboard-picker__close" aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;

    const total = this.dashboardPickerCounts[pickerType] ?? items.length;
    const footerHtml = this.renderPickerFooter(pickerType, items.length, total);

    if (!items.length) {
      const labels = {
        discovery: 'No discovery paths available.',
        practices: 'No active experiments yet.'
      };
      return `
        ${headerHtml}
        <div class="dashboard-picker__empty">${escapeHtml(labels[pickerType] || 'No items available.')}</div>
        ${footerHtml}
      `;
    }

    return `
      ${headerHtml}
      <div class="dashboard-recent-activity__list dashboard-picker__list">
        ${items.map(item => this.renderDashboardPickerItem(item, pickerType)).join('')}
      </div>
      ${footerHtml}
    `;
  }

  /**
   * Render the picker footer row: an "add" / "begin" button on the left and
   * a "Show all N <thing>" button on the right (the latter only when there
   * are more items than rendered).
   * @param {string} pickerType
   * @param {number} renderedCount
   * @param {number} total
   * @returns {string}
   */
  renderPickerFooter(pickerType, renderedCount, total) {
    const addLabels = {
      practices: 'Add an experiment',
      discovery: 'Guided discovery'
    };
    const showAllLabels = {
      discovery: `Show all ${total} paths`,
      practices: `Show all ${total} experiments`
    };

    const addLabel = addLabels[pickerType];
    const addBtnHtml = addLabel
      ? `<button
          type="button"
          class="dashboard-picker__footer-btn dashboard-picker__footer-btn--add"
          data-picker-add="${escapeHtml(pickerType)}"
        >
          <span class="dashboard-picker__footer-btn-label">${escapeHtml(addLabel)}</span>
        </button>`
      : '';

    const showAllBtnHtml = total > renderedCount
      ? `<button
          type="button"
          class="dashboard-picker__footer-btn dashboard-picker__footer-btn--show-all"
          data-picker-show-all="${escapeHtml(pickerType)}"
        >
          <span class="dashboard-picker__footer-btn-label">${escapeHtml(showAllLabels[pickerType] || `Show all ${total}`)}</span>
        </button>`
      : '';

    return `
      <div class="dashboard-picker__footer">
        ${addBtnHtml}
        ${showAllBtnHtml}
      </div>
    `;
  }

  /**
   * Map a picker type to the BadgeService section name used for card lookups.
   * @param {string} pickerType
   * @returns {string}
   */
  pickerTypeToBadgeSection(pickerType) {
    if (pickerType === 'discovery') return 'discovery';
    return 'practices';
  }

  /**
   * @param {Object} item
   * @param {string} [pickerType] - The picker the item belongs to, used to
   *   look up badge state in BadgeService.
   * @returns {string}
   */
  renderDashboardPickerItem(item, pickerType) {
    const badgeHtml = pickerType
      ? (window.badgeService?.renderCardIndicator?.(this.pickerTypeToBadgeSection(pickerType), item.id) || '')
      : '';
    const hasBadge = Boolean(badgeHtml);
    // Discovery items carry a `progress` field; practice items carry a `meta`
    // date string. Discovery always shows the progress bar in the meta slot so
    // the badge (if any) sits inline before the title. Practice items show the
    // badge in place of the date when present — the date is low-signal
    // ("Today" / "2 days") so the badge wins.
    const isDiscovery = item.progress !== undefined;
    let metaHtml;
    if (isDiscovery) {
      const progressHtml = `<div class="dashboard-recent-activity__progress">
          <div class="understanding__bar-track" style="--bar-width:${item.progress}%"><div class="understanding__bar-fill"></div></div>
          <span class="dashboard-recent-activity__progress-text">${item.progress}%</span>
        </div>`;
      metaHtml = `<span class="dashboard-picker__meta-stack">${progressHtml}${badgeHtml}</span>`;
    } else if (hasBadge) {
      metaHtml = badgeHtml;
    } else {
      metaHtml = `<span class="dashboard-picker__meta-text">${escapeHtml(item.meta || '')}</span>`;
    }

    // Discovery picker items carry the full path object as `item.raw`, so the
    // card image is available directly. Practice items have no image and fall
    // back to the icon SVG.
    const pickerImage = item.raw?.cardContent?.image || null;
    const iconSlotHtml = pickerImage
      ? `<span class="dashboard-recent-activity__item-icon dashboard-recent-activity__item-icon--image">
          <span class="path-disc dashboard-recent-activity__item-disc"><img src="assets/images/paths/${escapeHtml(pickerImage)}" alt="" class="dashboard-recent-activity__item-icon-img"></span>
        </span>`
      : `<span class="dashboard-recent-activity__item-icon">
          <svg class="dashboard-recent-activity__item-icon-svg dashboard-recent-activity__item-icon-svg--${escapeHtml(item.icon)}" width="20" height="20"><use href="#${escapeHtml(item.icon)}"/></svg>
        </span>`;

    return `
      <button
        type="button"
        class="dashboard-recent-activity__item dashboard-recent-activity__item--path dashboard-picker__item dashboard-picker__item--${item.type}"
        data-picker-item-id="${escapeHtml(item.id)}"
      >
        ${iconSlotHtml}
        <span class="dashboard-recent-activity__item-content">
          <span class="dashboard-recent-activity__item-title">
            <b>${escapeHtml(item.title)}</b>
          </span>
        </span>
        <span class="dashboard-recent-activity__item-meta">${metaHtml}</span>
        <span class="dashboard-recent-activity__item-arrow">
          <svg width="18" height="18" viewBox="0 0 16 16">
            <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
        </span>
      </button>
    `;
  }

  /**
   * @param {string} itemId
   */
  handleDashboardPickerItemClick(itemId) {
    const item = this.dashboardPickerItems.find(entry => entry.id === itemId);
    if (!item) return;

    const input = window.app?.modules?.input;
    input?.hideAutocomplete();
    this.clearDashboardPickerState();
    this.activeDashboardPromptId = null;

    if (item.type === 'discovery') {
      eventBus.emit('navigate', {
        page: 'chat',
        mode: 'DISCOVERY',
        pathId: item.raw.id,
        pathMetadata: {
          id: item.raw.id,
          name: item.raw.name,
          phase: item.raw.phase,
          description: item.raw.description || '',
          cardContent: item.raw.cardContent
        }
      });
      return;
    }

    if (item.type === 'practice') {
      eventBus.emit('navigate', {
        page: 'chat',
        mode: 'TREATMENT',
        subMode: 'checkin',
        practiceId: item.raw.id
      });
    }
  }

  /**
   * Navigate to the full listing page for a picker type when the user clicks
   * the trailing "Show all N …" item in a dropdown.
   * @param {string} pickerType
   */
  handlePickerShowAllClick(pickerType) {
    const input = window.app?.modules?.input;
    input?.hideAutocomplete();
    this.clearDashboardPickerState();
    this.activeDashboardPromptId = null;

    if (pickerType === 'discovery') {
      // Use page.js directly so the `?tab=paths` query reaches DiscoverySection.applyURLParams.
      page('/discovery?tab=paths');
    } else if (pickerType === 'practices') {
      eventBus.emit('navigate', { page: 'practices' });
    }
  }

  /**
   * @param {string|Date|null|undefined} value
   * @returns {number}
   */
  getTimestamp(value) {
    if (!value) return 0;
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  /**
   * Format an activity timestamp for compact dashboard picker metadata.
   * @param {string|Date|null|undefined} value
   * @returns {string}
   */
  formatActivityDate(value) {
    const timestamp = this.getTimestamp(value);
    if (!timestamp) return '';

    const now = Date.now();
    const diffDays = Math.max(0, Math.floor((now - timestamp) / 86400000));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days`;

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    }).format(new Date(timestamp));
  }

  /**
   * Return to dashboard from an active session
   * Reloads data and re-renders content.
   * Event listeners are delegated from persistent containers and bound once
   * in initialize(), so they survive re-renders — no rebinding needed here.
   * NOTE: Visibility is handled by PageManager, not DashboardManager
   */
  async returnToDashboard() {
    // Reset the Recommended/Recent/Starred view switcher synchronously,
    // BEFORE the first await — so a library load started on a PREVIOUS visit
    // can never seed this new visit's cache (its resolution is discarded by
    // `_loadLibraryItems`'s promise-identity guard). This gives "resets every
    // visit" and "fresh data every visit" together, for free.
    this.activityView = 'recommended';
    this.libraryItems = null;
    this._libraryLoadPromise = null;
    this.libraryLoadFailed = false;
    this.activityVisibleCount = 10;
    this._closeActivityViewMenu();

    // Refresh BadgeService data (Foundation status + nav card badges)
    if (window.badgeService) {
      await window.badgeService.refresh();
    }

    // Reload nav card status from refreshed BadgeService
    this.loadNavCardStatus();

    // Reload recent activity (may have changed during session)
    await this.loadRecentActivity();

    // Invalidate picker caches so the suggested-prompt dropdowns reflect any
    // progress made during the session (discovery %, practice counts). These
    // are memoized on first fetch and otherwise never refresh.
    this.discoveryPromptItems = null;
    this.practicePromptItems = null;
    this.dashboardPickerCounts = {};
    await this.loadDashboardPickerCounts();

    // Re-render dashboard with fresh data
    this.render();

    // Re-apply counts and badge dots (render() wipes innerHTML)
    this.updateSuggestedPromptsState();

    // Re-render recent activity
    this.renderRecentActivity();
  }
}
